import { createStore } from 'vuex'
import { db } from '@/firebase'
import firebase from 'firebase/app'

// 選択Undo: highlightedChibansのスナップショットをundoスタックに保存
// initApp.ts, draw.js, ext-chiban.vue等から呼ばれるグローバルヘルパー
export function pushSelectionUndo(store) {
  const st = store.state
  const stack = st.selectionUndoStack
  stack.push({
    chibans: new Set(st.highlightedChibans),
    features: new Map(st.highlightedFeatures),
    lastChiban: st.lastHighlightedChiban,
  })
  if (stack.length > 50) stack.shift()
  st.selectionRedoStack.length = 0
}

// UIDをGETパラメータに含めず、POST(FormData)で送信するヘルパー
function postWithUid(url, uid, extraParams = {}) {
  const fd = new FormData()
  fd.append('uid', uid)
  Object.entries(extraParams).forEach(([k, v]) => fd.append(k, v))
  return fetch(url, { method: 'POST', body: fd })
}

// === Message Dialog 用 Vuex モジュール（このファイル内に内蔵） ===
const messageDialogModule = {
  namespaced: true,
  state: () => ({
    // 複数ダイアログを id ごとに保持
    registry: {},

    // ★ 追加: 直近のイベント（グローバル通知用）
    // 例: { type:'dont-show-set', id:'help', key:'oh3.hideJobTips', ts: 173.0 }
    lastEvent: null,
    // ★ 追加: 変更検知用のシーケンス（watch が楽）
    eventSeq: 0,
  }),

  getters: {
    // 使用例: this.$store.getters['messageDialog/entry']('help')
    entry:
      (state) =>
      (id = 'default') => {
        return state.registry[id] || { open: false, title: '', contentHtml: '', options: {} }
      },

    // ★ 追加: “次回から表示しない” が有効かを問い合わせ
    // 引数 key を省略した場合は entry(options.dontShowKey) を使う
    isSuppressed:
      (state, getters) =>
      (id = 'default', key) => {
        try {
          const k = key || getters.entry(id)?.options?.dontShowKey
          if (!k) return false
          return localStorage.getItem(k) === '1'
        } catch (_) {
          return false
        }
      },
  },

  mutations: {
    SET_ENTRY(state, { id = 'default', patch = {} }) {
      const cur = state.registry[id] || {}
      state.registry = { ...state.registry, [id]: { ...cur, ...patch } }
    },
    SET_OPEN(state, { id = 'default', open }) {
      const cur = state.registry[id] || {}
      state.registry = { ...state.registry, [id]: { ...cur, open: !!open } }
    },

    // ★ 追加: イベント格納＆シーケンス更新
    SET_LAST_EVENT(state, ev) {
      state.lastEvent = ev
      state.eventSeq++
    },
  },

  actions: {
    // まとめて開く（タイトル/本文/見た目を同時注入）
    open({ commit, getters }, { id = 'default', title, contentHtml, options } = {}) {
      const key = options && options.dontShowKey
      // ★ どちらかでOFFにしてあれば開かない
      try {
        if (key && localStorage.getItem(key) === '1') return
      } catch (_) {}
      commit('SET_ENTRY', { id, patch: { title, contentHtml, options, open: true } })
    },

    // ★ 追加: 抑止判定つきで開く（呼び出し側が boolean を受け取れる）
    // 返値: true=開いた / false=抑止された
    openOnce({ dispatch }, payload = {}) {
      const { options } = payload || {}
      const key = options && options.dontShowKey
      try {
        if (key && localStorage.getItem(key) === '1') return false
      } catch (_) {}
      dispatch('open', payload)
      return true
    },

    close({ commit }, { id = 'default' } = {}) {
      commit('SET_OPEN', { id, open: false })
    },

    setOpen({ commit }, { id = 'default', open }) {
      commit('SET_OPEN', { id, open })
    },

    // 表示中に内容だけ差し替えたいとき
    update({ commit }, { id = 'default', patch = {} } = {}) {
      commit('SET_ENTRY', { id, patch })
    },

    // ★ 追加: “次回から表示しない” をストア API として提供
    // 例) this.$store.dispatch('messageDialog/setDontShow', { id:'help', key:'oh3.hideJobTips' })
    setDontShow({ commit }, { id = 'default', key }) {
      if (!key) return
      try {
        localStorage.setItem(key, '1')
      } catch (_) {}
      // グローバルイベントとして公開
      commit('SET_LAST_EVENT', { type: 'dont-show-set', id, key, ts: Date.now() })
    },

    // ★ 追加: 解除（再表示）
    clearDontShow({ commit }, { id = 'default', key }) {
      if (!key) return
      try {
        localStorage.removeItem(key)
      } catch (_) {}
      commit('SET_LAST_EVENT', { type: 'dont-show-clear', id, key, ts: Date.now() })
    },

    // ★ 追加: 任意イベントの公開（コンポーネントから叩く用）
    // 例) this.$store.dispatch('messageDialog/publishEvent', { type:'dont-show-set', id, key, ts:Date.now() })
    publishEvent({ commit }, ev) {
      commit('SET_LAST_EVENT', ev)
    },
  },
}
const defaults = () => ({
  enabled: true,
  maps: { map01: false, map02: false },
  params: {
    method: 'multidirectional',
    exaggeration: 0.6,
    direction: 315,
    altitude: 35,
    mdDirections: [270, 315, 0, 45],
    mdAltitudes: [30, 30, 30, 30],
    mdHighlights: ['#fff4cc', '#ffeaa1', '#eaffd0', '#dff8ff'],
    mdShadows: ['#3b3251', '#2e386f', '#2a2a6e', '#3a2d58'],
  },
})

const defaultKyusekiPreviewState = () => ({
  geoJson: { type: 'FeatureCollection', features: [] },
  imageUrl: '',
  imageSize: { w: 0, h: 0 },
  mapBase: 'seamlessphoto',
  mode: 'image',
  timestamp: 0,
})
export default createStore({
  state: {
    drawFeatureId: '',
    currentDrawFeature: null, // 現在編集中のドロー地物（FloatingWindow用）
    useVuetifyPopup: true, // Vuetify版ポップアップを使用するフラグ（初期値：false）
    closeLineToPolygon: true, // フリーハンドを閉じてポリゴンにする（初期値：true）
    drawInteractionPriority: 'snap', // クリック時の優先動作: snap/edit/info
    drawAutoOpenMode: true, // 新規作成後のFW: true=自動開く, false=開かない
    printTileEnabled: false, // A0相当の分割印刷
    printTileSize: 'a4', // a4 or a3
    clientVersion: 3.305,
    userSetsVersion: 0, // ユーザーセット更新通知カウンター（Dialog-popular-sets → Dialog-layer連携用）
    // マップAI choropleth/contour/extrusion の地図上凡例
    // null=非表示、{title, items:[{color,label}], unit?, source?} で表示
    choroplethLegend: null,
    // 直近に描画した admin choropleth のメタ（県→市町村ドリルダウン用）
    // { level, title, indicatorName? } を保持
    lastAdminChoroplethMeta: null,
    tokenBalance: null, // トークン残高（tokenService.tsが更新、Dialog-menuが参照）
    tokenLimitExceeded: null, // TOKEN_LIMIT_EXCEEDED時の情報（consumeTokenが設定、App.vueが表示）
    tokenLoginRequired: false, // ログイン要求ダイアログ表示フラグ（ENFORCE_TOKEN_LIMIT=true時）
    greenSelectedCategory: null, // 緑のオープンデータ：選択中カテゴリ（null=全表示, string=単一カテゴリ）
    isSplash: null,
    showSakuraEffect: false, // 桜エフェクト表示フラグ
    hillshade: defaults(),
    mapReady: false,
    recentLayerIds: [], // 最近使用したレイヤーID (最大5件)
    initialParamString: null, // 起動時のパラメータ文字列を保存
    disablePollingForTest: false, // ポーリングを一時停止するフラグ（テスト用）
    pendingAiShareId: null, // AI会話共有URLからの復元用ID
    aiShareListRefreshTrigger: null, // AI会話リスト更新トリガー
    // AI分析共有地物（URL共有で複数地物を共有）
    shareFeatures: [], // { id, aiType, geometry, properties }
    shareFeaturesLayerAdded: false, // レイヤー追加済みフラグ
    highlightedRankingPointId: null, // AI物件調査レポート+ でマーカークリック時にハイライトする pointId
    // AI物件調査レポート+ の URL共有用状態 (Dialog が開いているとき書き込まれ、permalinkState が読む)
    // 形式: { points: [{lng,lat}], presetKey: 'balanced'|...|'custom', weights: {access,convenience,...} } | null
    mprdShareState: null,
    refreshShareFeaturesFire: false, // shareFeatures再表示トリガー（addLayers後に発火）
    // オンボーディング状態
    onboarding: {
      active: false, // オンボーディング中かどうか
      currentStep: 0, // 現在のステップ（0: レイヤー選択を促す）
      completed: false, // 完了したかどうか
    },
    lastShortUrlParam: null, // 最後に生成したショートURLパラメータ
    shortUrlTimerId: null, // ショートURL生成タイマーID
    pendingFile: null,
    commonDialog: {
      versionMessage: false,
      fileDropOpen: false,
      fileDropExt: 'pdf',
      fileDropFiles: [],
      geoReferenceOpen: false,
      watchPositionOpen: false,
    },
    fileDropFlow: {
      open: false,
      files: [],
      action: null,
      mode: 'user-select',
    },
    homukyokuXmlDialog: {
      open: false,
      files: [],
    },
    sokuiHeader: [
      '点名',
      'X',
      'Y',
      '標高',
      'アンテナ高',
      '標高（アンテナ位置）',
      '楕円体高',
      'XY較差',
      '座標系',
      '緯度',
      '経度',
      '所在',
      'ノート',
      'タイプ',
      'パス',
      '測位回数',
      '測位日時',
    ],
    disabledForSokui: true,
    printMap: 'map01',
    pdfLoading: false, // PDF生成中フラグ
    aiEnhanceProcessing: false, // AI画像鮮明化処理中フラグ
    aiEnhanceImageSource: null, // AI鮮明化画像のソース情報（coordinates, outputUrl）
    aiEnhanceSaving: false, // AI鮮明化画像保存中フラグ
    aiEnhanceDialogVisible: false, // AI鮮明化完了ダイアログ表示フラグ
    aiEnhanceSaveName: '', // AI鮮明化保存時の名前
    isContextMenu: true,
    isOverseas: false, // 海外からのアクセスか（ドローツールをアイコン表示）
    showCrosshair: true,
    showNorthArrow: false,
    isKuiuchi: false,
    geo: null,
    demTint: null,
    mode: 'step', // 将来: 'linear' でグラデ
    level: 0, // しきい値[m]
    palette: {
      // 陸＝自然系18段、海＝青系18段（いま使ってるやつ）
      aboveDomain: [0, 2, 5, 10, 20, 35, 60, 90, 130, 200, 300, 450, 700, 1100, 1600, 2200, 3000, 3600],
      aboveRange: [
        '#eaf7e3',
        '#dbf0d1',
        '#c7e6b3',
        '#aede95',
        '#95d27a',
        '#7ec663',
        '#cfc48e',
        '#d7bc82',
        '#dfb376',
        '#e4a768',
        '#d99759',
        '#c88749',
        '#b2733e',
        '#9a6034',
        '#84542d',
        '#bfbfbf',
        '#eaeaea',
        '#ffffff',
      ],
      belowDomain: [0, 1, 2, 3, 5, 8, 12, 20, 35, 60, 100, 160, 260, 420, 650, 1000, 1600, 2500],
      belowRange: [
        '#eaf6ff',
        '#d7eeff',
        '#c3e5ff',
        '#b0dcff',
        '#9bd1ff',
        '#86c6ff',
        '#71bbff',
        '#5aafff',
        '#439fff',
        '#2f8fe0',
        '#217fcb',
        '#1a70b6',
        '#145fa0',
        '#0f4f8a',
        '#0b416f',
        '#08365b',
        '#072b46',
        '#051f34',
      ],
    },
    isRadius2002: false,
    isRadius200: false,
    oh200mGeoJSON: null,
    oh200mIds: [],
    // 独自コンファーム
    showConfirm: false,
    confirmMessage: '',
    confirmProps: {
      title: '確認',
      okText: 'OK',
      cancelText: 'キャンセル',
      persistent: true,
      width: 420,
      color: 'primary',
      icon: null,
    },
    confirmResolve: null,
    // ログインダイアログトリガー（他コンポーネントからログインダイアログを開くため）
    loginDialogTrigger: 0, // インクリメントで発火
    // ここまで
    hitText: '',
    iframeVDIalog: false,
    isDrawFit: false,
    isFromIframe: false,
    isIframe: false,
    skipTerms: false, // URL共有時の利用規約スキップフラグ
    urlParamsReady: false, // parseUrlParams完了フラグ（TermsAgreementDialogのタイミング制御用）
    is4ScreenMode: false, // 4画面モード内のiframeかどうか
    // 4画面モードの位置マッピング: 論理位置(0-3) -> マップ名
    // 0=左上, 1=右上, 2=左下, 3=右下
    fourScreenPositions: ['map01', 'map02', 'map03', 'map04'],
    // マップ連動モード: true=連動, false=個別
    mapSyncMode: true,
    // 各マップの個別座標状態（個別モード時に使用）
    mapIndividualStates: {
      map01: { lng: null, lat: null, zoom: null, pitch: null, bearing: null },
      map02: { lng: null, lat: null, zoom: null, pitch: null, bearing: null },
      map03: { lng: null, lat: null, zoom: null, pitch: null, bearing: null },
      map04: { lng: null, lat: null, zoom: null, pitch: null, bearing: null }
    },
    oh3App: null,
    map01Pitch: 0,
    map01Bearing: 0,
    map02Pitch: 0,
    map02Bearing: 0,
    prevGeojsons: [],
    prevGeojson: null,
    selectedLngLat: null,
    currentLocation: null, // 現在地の座標 { lng, lat }
    currentLocationMarker: null, // 現在地のマーカーオブジェクト
    showDrawConfrim: false,
    noProgress: false,
    filter360: [
      'all',
      ['==', ['get', 'is_pano'], true],
      // ["==", ["get", "compass_angle"], 0]
    ],
    mapillaryTytle: 'mapillary',
    mapillaryType: 'mapillary',
    mapillaryIsNotArrow: false,
    mapillaryStartDate: '',
    mapillaryEndDate: '',
    mapillaryUserName: '',
    mapillaryFeature: null,
    mapillaryImageId: null,
    mapillaryZindex: 0,
    is360Pic: false,
    targetSeq: '',
    isLabelUpdated: false,
    dialogForVersion: false,
    isStandalone: false,
    isIOS: false,
    mapSpeedProfile: 'normal',
    isSmall1000: false,
    isSmall500: false,
    showPcMode: localStorage.getItem('oh3_showPcMode') === 'true',
    bottomSheetActive: false, // ボトムシートモードがアクティブかどうか（スマホ用）
    isFreehandCanvasActive: false, // スケッチモードがアクティブかどうか
    // フリーメモ機能
    isFieldNoteMemoMode: false, // フリーメモモード（地図クリック待機）
    isFieldNoteMemoActive: false, // フリーメモ全画面表示中
    fieldNoteMemoCoords: null, // フリーメモのクリック座標 { lng, lat }
    fieldNoteMemoPointId: null, // フリーメモのポイントID
    fieldNoteMemoData: null, // フリーメモのキャンバスデータ（Base64）
    pendingFieldNoteMemoMode: false, // 右クリックメニューからフリーメモを開く時のフラグ
    isAndroid: false,
    drawFeature: null,
    configFeature: null,
    baseConfigFeature: {
      type: 'Feature',
      properties: {
        id: 'config',
        'title-text': '',
        'font-size': 30,
        'fill-color': 'black',
        direction: 'vertical',
        visible: true,
        opacity: 1,
      },
      // "geometry": {
      //   "type": "Point",
      //   "coordinates": [139.767125, 35.681236]
      // }
    },
    isMine: false,
    isEditableForVSelect: 'private', // 'public' | 'private-edit' | 'private'
    isEditable: true,
    isPrivateDraw: false, // プライベートドロー（URLにドローを含めない）
    isUsingServerGeojson: false,
    myDrawUndoCount: 0, // マイドロー用Undoスタックのカウント
    myDrawRedoCount: 0, // マイドロー用Redoスタックのカウント
    drawShareId: '',
    selectedFile: null,
    previewUrl: '',
    pictureUrl: '',
    drawGeojsonId: '',
    geojsonName: '',
    pmtilesFirstSave: false,
    pmtilesMaximum: 17,
    pmtiles0Name: '',
    pmtilesLabel: '',
    pmtilesStyle: {},
    propnames: [],
    pmtiles0Id: 0,
    // マイマップ機能 (Google My Maps クローン)
    activeMymap: null,                // { id, name, description, url, filename, geojson }
    mymapSelectedFeatureIdx: null,    // 選択中フィーチャーの index
    mymapSaveStatus: 'idle',          // 'idle' | 'pending' | 'saving' | 'saved' | 'error'
    mymapLastSavedAt: null,
    mymapDrawMode: null,              // null | 'point' | 'line' | 'polygon'
    mymapDrawPreview: null,
    floatingWindows: {},
    // 到達圏プリセット: { lng, lat, name, autoRun }
    isochronePreset: null,
    roadIsochronePreset: null, // 道路到達圏プリセット: { lng, lat, profile }
    // コンテキストヘルプ: { title, page } or null
    contextHelpContext: null,
    // グローバル画像プレビュー（popup.jsなどからも呼び出せる）
    globalMediaPreview: { open: false, src: '', kind: 'image', filename: '' },
    kyusekiPreview: defaultKyusekiPreviewState(),
    mw5Previews: [], // [{id, title, extent}] 旧版地形図プレビューFW（無限生成）
    kyusekiInitialFile: null,
    touhonOverlayFile: null,
    pendingPhotoMemoFiles: null, // DD経由の写真メモファイル
    featureForOfflineBbox: null,
    isOffline: false,
    dbConnectionFailed: false, // DB接続失敗時にtrue（メンテナンスバナー表示用）
    isNetworkOffline: false, // ネットワークがオフラインかどうか（navigator.onLine）
    isOfflineModeAuto: false, // オフラインモードが自動でONになったかどうか
    dialogForOffline2: false,
    dialogForOffline: false,
    dialogForPicture: false,
    vpsServerName: 'kenzkenz.net',
    drawFire: false,
    drawOpacity: 1,
    drawVisible: true,
    drawVisiblePerMap: { map01: true, map02: true, map03: true, map04: true },
    isPrint: false,
    isLassoSelected: false,
    lassoGeojson: null,
    lassoBackupGeojson: null,
    lassoFwPosition: null, // { left, top } - BBOXの右側または左側
    toggleCheckFire: false,
    printTitleText: '',
    printTitleTexts: { map01: '', map02: '', map03: '', map04: '' },
    printDate: '', // 印刷用日付
    textPx: 30,
    titleColor: 'black',
    titleDirection: 'a4-vertical',
    // 法定図面風PDF出力オプション
    pdfProOptions: {
      showBorder: true, // 図郭（枠線）表示
      showNorthArrow: true, // 北矢印表示
      showFooter: true, // フッター表示
      showChibanList: false, // 地番一覧を2ページ目に追加
      authorName: '', // 作成者名
      locationName: '', // 所在地（空の場合はaddressを使用）
    },
    finishLineFire: false,
    popupDialog: false,
    popupHtml: '',
    elevationGeojson: '',
    elevation: '',
    distance: '',
    elevationDrawMode: null, // 'line', 'free', null (標高図作成モード)
    elevationHoverPoint: null, // { distance, elevation } D3チャート/地図ホバー連動
    isMoveMode: false,

    currentTextColor: 'black',
    currentPointColor: 'black',
    currentTextSize: 16,
    currentTextLabelType: '1',

    currentCircleRadius: 0,
    currentCircleColor: 'black',
    currentCircleLineColor: 'black',
    currentCircleLineWidth: 1,

    currentPolygonColor: 'black',

    currentLineColor: 'black',
    currentLineWidth: 5,
    currentLineLabelType: 'start',
    currentLineCalcCheck: (() => {
      try {
        const saved = localStorage.getItem('oh3.currentLineCalcCheck')
        return saved !== null ? Number(saved) : 0
      } catch (_) { return 0 }
    })(),
    currentPolygonAreaCheck: (() => {
      try {
        const saved = localStorage.getItem('oh3.currentPolygonAreaCheck')
        return saved !== null ? saved === 'true' : false
      } catch (_) { return false }
    })(),
    currentArrowColor: 'arrow_black',
    currentArrowType: 'end', // 矢印タイプ: 'end', 'none', 'both'

    currentFreeHandWidth: '',
    currentFreeHandColor: 'black',
    currentFreeHandKeikoColor: 'rgba( 28,  28,  28, 0.5)',

    // 前回の地物スタイルを保存（次の地物作成時に引き継ぐ）
    lastLineStyle: null, // { color, colorName, 'line-width', label, arrowType, arrowColor }
    lastPolygonStyle: null, // { color, colorName, 'line-width', label, opacity }
    lastPointStyle: null, // { color, 'point-color', label, 'text-size' }
    lastCircleStyle: null, // { color, colorName, 'line-width', radius }
    lastFreeHandStyle: null, // { color, 'keiko-color', 'line-width' }
    lastTextStyle: null, // { color, label, 'text-size', textAnchor }

    tgtFeature: null,
    pointSima: false,
    seaLevel: {
      map01: 0,
      map02: 0,
    },
    seaLevelTheme: {
      map01: 'ocean',
      map02: 'ocean',
    },
    hypsoLevel: {
      map01: 0,
      map02: 0,
    },
    hypsoScheme: {
      map01: 'standard',
      map02: 'standard',
    },
    saveHistoryFire: false,
    jdpCode: '',
    jdpCoordinates: [],
    editEnabled: false,
    editingFeatureId: null,  // 編集中の地物ID（地物単位の変形・移動用）
    isCursorOnPanel: false,
    panelHoverCount: 0,
    circle200Chk: false,
    updatePermalinkFire: false,
    snapshotRefreshFire: false, // 簡単SSリスト更新トリガー
    snapshotNewItem: null, // 楽観的更新: 保存直後のスナップショットデータ
    // 不動産調査レポート
    showRealEstateReportDialog: false,
    realEstateReportLocation: null,
    id: '',
    coordinates: [],
    gazoName: '',
    /**
     * ドロー関係フラグ
     */
    historyCursor: -1,
    isApplyingHistory: false,
    suppressDrawPitchReset: false,
    isDrawUndoRedo: false,
    isDrawLassoForChibanzu: false,
    isDrawLassoForTokizyo: false,
    isDrawLassoForTokizyo2026: false,
    isDraw: false,
    drawButtonsVisible: true,
    drawInfoVisible: false, // 情報ボックスの表示状態
    isDrawLasso: false,
    isDrawFix: false,
    isDrawDuplicate: false, // 複製モード
    duplicateSourceFeature: null, // 複製元の地物
    isDrawFree: false,
    isFreehandCanvasMode: false, // 強化版フリーハンド（キャンバスモード）
    isDrawPolygon: false,
    isDrawLine: false,
    isDrawCircle: false,
    isDrawPoint: false,
    isDrawText: false, // テキスト専用モード
    textInputDialogOpen: false, // テキスト入力ダイアログの表示状態
    textInputCoordinates: null, // テキスト入力する座標
    textInputColor: '#000000', // テキストの色
    textInputFontSize: 16, // テキストのサイズ
    drawContinuousMode: false, // 連続描画モード
    thumbnailAlwaysVisible: true, // サムネイルマーカー常時表示
    rightDrawerTitle: '',
    selectedPublic: 0,
    selectedKaiji2: 1,
    geojsonFile: null,
    chibanzuPrefCode: '',
    chibanzuCityCode: '',
    chibanzuPropaties: {},
    chibanzuGeojson: [],
    showChibanzuDialog: false,
    popupFeature: {},
    popupFeatureProperties: {},
    popupFeatureCoordinates: [],
    soloFlg: false,
    mapillaryDialog: false,
    clickedCoordinates: [],
    selectedPointColor: '#000000', // ✅ 追加: 現在のポイント色
    selectedLayerId: null, // 選択中のレイヤーIDを追加
    currentGroupId: '',
    currentGroupLayers: [],
    pointLayers: [
      {
        id: 'layer1',
        name: 'ポイントレイヤー1',
        color: '#000000',
        visible: true,
        features: [{ id: 'abc123', geometry: {}, properties: {} }],
      },
    ],
    currentPointLayerId: 'layer1', // ← 現在選択されているレイヤーを保持
    // selectedPointFeature: null,
    currentPointLayerIndex: 0,
    // -----------------------------------------------------------------------------------------------------------------
    map01: null,
    map02: null,
    map03: null,
    map04: null,
    activeDrawMap: 'map01', // ドローイベント発生元マップ（map02からの操作時に 'map02' に設定）
    suppressExtAutoOpen: false, // 起動時レイヤー復元中はextダイアログを自動表示しない
    snackbar: false,
    snackbarText: '',
    snackbarType: '', // 'sima' or 'dxf'
    snackbarForGroup: false,
    snackbarForGroupText: false,
    geojsonId: '',
    userId: '',
    myNickname: '',
    // 教員情報
    isTeacher: false,
    teacherInfo: null, // { id, firebase_uid, name, school_name, created_at }
    showTeacherDashboard: false,
    // 組織オーナー情報（新）
    isOrganizationOwner: false,
    organizationInfo: null, // { id, name, plan_id, point_balance, max_seats, ... }
    showOrganizationAdmin: false,
    // 組織メンバー情報（新）
    isOrganizationMember: false,
    organizationMemberInfo: null, // { id, nickname, email, status, role, org_name, ... }
    // 組織グループ一覧（新）
    orgGroups: [], // [{ group_id, group_name }]
    showWhatsNew: false,
    // 生徒情報（クラス所属）
    isStudent: false,
    studentClasses: [], // [{ class_id, class_name, teacher_name, teacher_school }]
    // 授業モード（生徒側の状態）
    lessonMode: {
      active: false,        // 授業中かどうか
      syncEnabled: false,   // ズーム・パン同期中か
      className: '',        // クラス名
      teacherName: '',      // 先生の名前
      classId: null         // クラスID
    },
    studentSyncLocked: false, // 授業同期中の生徒操作ロック状態
    snapshotRestoredAt: null, // スナップショット/URL復元時刻（授業同期用）
    searchHistory: [],
    favoriteLocations: [], // お気に入り地点 [{name, lat, lng, zoom, createdAt}]
    savedMarkers: [], // 保存されたマーカー [{id, lng, lat, title, marker_type, created_at}]
    currentGroupName: null,
    map2Flg: false,
    // ポップアップ同期（複数画面で同時ポップアップ）
    isFullScreenPopup: true,
    isUnder500: false,
    showDrawDrawer: false,
    showDrawListDrawer: false,
    showAboutDrawer: false,
    isWatching: false, // 現在地連続取得中かどうか
    isMagnifying: false, // 虫眼鏡モード
    isSketchMode: false, // スケッチ風フィルター
    isTerrainEnabled: false, // 3D地形表示が有効かどうか
    isDiorama3D: false, // 3Dジオラマ表示
    dioramaDemSource: 'gsj', // ジオラマ用DEMソース: 'gsj' or 'shimane'
    isCrossSection3D: false, // 3D断面図表示
    _suppressElevation3DAutoOpen: false, // URL復元時の3D自動表示を抑制
    restoring3DOverlay: false, // URL復元時の3Dビューローディングオーバーレイ
    _3dCameraState: null, // { px,py,pz,tx,ty,tz,exag,mini } URL復元用カメラ状態
    showPointInfoDrawer: false,
    showRightDrawer: false,
    showChibanzuDrawer: false,
    selectedPointFeature: null,
    groupGeojson: { type: 'FeatureCollection', features: [] },
    dialogs: {
      chibanzuListDialog: {
        map01: {
          name: 'chibanzuListDialog',
          style: { top: '65px', left: '10px', 'z-index': 1, height: 'auto', width: '350px', display: 'none' },
        },
        map02: {
          name: 'chibanzuListDialog',
          style: { top: '65px', left: '10px', 'z-index': 1, height: 'auto', width: '250px', display: 'none' },
        },
      },
      drawConfigDialog: {
        map01: {
          name: 'drawConfigDialog',
          style: { top: '10px', left: '30px', 'z-index': 1, height: 'auto', width: '350px', display: 'none' },
        },
        map02: {
          name: 'drawConfigDialog',
          style: { top: '10px', left: '30px', 'z-index': 1, height: 'auto', width: '350px', display: 'none' },
        },
      },
      drawShareDialog: {
        map01: {
          name: 'drawShareDialog',
          style: { top: '10px', left: '30px', 'z-index': 1, height: 'auto', width: '350px', display: 'none' },
        },
        map02: {
          name: 'drawShareDialog',
          style: { top: '10px', left: '30px', 'z-index': 1, height: 'auto', width: '350px', display: 'none' },
        },
      },
      shareDialog: {
        map01: {
          name: 'shareDialog',
          style: { top: '65px', left: '10px', 'z-index': 1, height: 'auto', width: '350px', display: 'none' },
        },
        map02: {
          name: 'shareDialog',
          style: { top: '65px', left: '10px', 'z-index': 1, height: 'auto', width: '250px', display: 'none' },
        },
      },
      menuDialog: {
        map01: {
          name: 'menuDialog',
          style: { top: '65px', left: '10px', 'z-index': 1, height: 'auto', width: '300px', display: 'none' },
        },
        map02: {
          name: 'menuDialog',
          style: { top: '65px', left: '10px', 'z-index': 1, height: 'auto', width: '300px', display: 'none' },
        },
      },
      myroomDialog: {
        map01: {
          name: 'myroomDialog',
          style: { top: '65px', left: '10px', 'z-index': 1, height: 'auto', width: '250px', display: 'none' },
        },
        map02: {
          name: 'myroomDialog',
          style: { top: '65px', left: '10px', 'z-index': 1, height: 'auto', width: '250px', display: 'none' },
        },
      },
      layerDialog: {
        map01: {
          name: 'layerDialog',
          style: {
            top: '65px',
            left: '10px',
            'z-index': 1,
            height: 'auto',
            'min-width': '350px',
            display: 'none',
            'overflow-y': 'auto',
          },
        },
        map02: {
          name: 'layerDialog',
          style: {
            top: '65px',
            left: '10px',
            'z-index': 1,
            height: 'auto',
            'min-width': '350px',
            display: 'none',
            'overflow-y': 'auto',
          },
        },
      },
      pyramidDialog: {
        map01: {
          name: 'pyramidDialog',
          style: { top: '65px', left: '10px', 'z-index': 1, height: 'auto', 'min-width': '150px', display: 'none' },
        },
        map02: {
          name: 'pyramidDialog',
          style: { top: '56px', left: '10px', 'z-index': 1, height: 'auto', 'min-width': '150px', display: 'none' },
        },
      },
      extHighway: {
        map01: {
          name: 'extHighway',
          style: { top: '65px', left: '10px', 'z-index': 1, height: 'auto', 'min-width': '150px', display: 'none' },
        },
        map02: {
          name: 'extHighway',
          style: { top: '65px', left: '10px', 'z-index': 1, height: 'auto', 'min-width': '150px', display: 'none' },
        },
      },
    },
    dialogsInfo: {
      map01: [],
      map02: [],
    },
    // RRIM直接マウント用（ext経由を廃止）複数同時対応
    rrimItems: {
      map01: [],
      map02: [],
    },
    ext: {
      map01: [],
      map02: [],
      map03: [],
      map04: [],
    },
    selectedLayers: {
      map01: [],
      map02: [],
      map03: [],
      map04: [],
    },
    // お気に入りレイヤー（レイヤーIDの配列）
    favoriteLayerIds: [],
    dialogs2: {
      map01: [],
      map02: [],
      map03: [],
      map04: [],
    },
    urlForX: '',
    url: 'dumy',
    dialog2Id: 1,
    dialogMaxZindex: 10,
    openPlanTab: 0,
    cdArea: '',
    syochiikiName: '',
    koureikaritu: '',
    heikinnenrei: '',
    kokuchoYear: '',
    estatDataset: null,
    jinkosuiiDataset: [],
    jinkosuiiDatasetEstat: [],
    addLayersRequested: {
      map01: 0,
      map02: 0,
      map03: 0,
      map04: 0,
    },
    terrainLevel: 1,
    isPitch: false,
    lngRange: [],
    latRange: [],
    lngRange2: [],
    latRange2: [],
    secondDivStyle: { height: '390px', overflow: 'auto', 'user-select': 'text' },
    // extFire: 廃止済み - 各extコンポーネントのmounted()で初期適用する方式に移行
    reorderLayersFire: false, // extがカスタムレイヤー追加後にreorderLayers()を呼ぶためのトリガー
    osmFire: false,
    fetchImagesFire: false,
    fetchSenmeikaFire: false,
    fetchPhotoMemoFire: false,
    photoMemoLocalUpdate: null, // { id: number, updates: { memo?, ocr_text? } } - ドローFWからの同期用
    photoSketchTarget: null, // { photoMemoId, pictureUrl, sketchData, uid } - 写真スケッチFW用
    textDetailTarget: null, // { item, type, userId } - OCR/メモ詳細FW用
    MESH_ID: '',
    isEstat: true,
    suikeiYear: '',
    popupAddress: '',
    stationName: '',
    kei: '',
    prefId: '',
    zahyokei: '',
    zahyokeiShape: 'WGS84',
    isKyusekiDialogOpen: false,
    cadType: '2d',
    zeniGeojson: '',
    ntripGeojson: '',
    mindenGeojson: '',
    isRenzoku: true,
    lastHighlightedChiban: null,
    hoveredChibanFromFW: null, // FloatingWindowからのホバー用
    lockedChibanFromFW: null, // FloatingWindowからのクリックロック用
    adjacentHighlightMode: false,
    highlightedChibans: new Set(),
    highlightedChibansDetails: new Map(), // key: "筆ID_地図名", value: { fudeId, chiban, address, center, properties }
    highlightedFeatures: new Map(), // key: targetId, value: GeoJSON feature（geometry含む）- ハイライトレイヤー用
    // 選択Undo/Redo用スタック（draw.jsからも使えるようstoreに置く）
    selectionUndoStack: [],
    selectionRedoStack: [],
    highlightedSimas: new Set(),
    highlightedDxfs: new Set(),
    dialogForSima: false,
    simaText: '',
    simaTextForUser: '',
    kmlText: '',
    geojsonText: '',
    dxfText: '',
    gpxText: '',
    simaTextZahyoukei: '',
    dialogForSimaApp: false,
    dialogForPng2App: false,
    dialogForJpgApp: false,
    dialogForGeotiffApp: false,
    dialogForGeotiff2App: false,
    dialogForPngApp: false,
    dialogForApp: false,
    dialogForImage: false,
    dialogForLogin: false,
    initialLoginMode: 'login', // 'login' or 'signup'
    dialogForGroup: false,
    dialogForLink: false,
    isMenu: false,
    simaOpacity: 0,
    simaFire: false,
    tiffAndWorldFile: null,
    uploadedImage: '',
    uploadedVector: '',
    extLayerName: '独自レイヤー',
    extLayer: '',
    isClickPointsLayer: false,
    drawGeojsonText: '',
    clickGeojsonText: '',
    clickCircleGeojsonText: '',
    // 授業モード: ext コンポーネント同期用の汎用ステート
    // { [extId]: { ...params } } — Teacher が書き込み、Student が読み込み
    syncedExtState: {},
    clickCircleGeojsonTextMyroom: '',
    transparent: '1',
    resolution: 23,
    loading: false,
    loading2: false,
    loading3: false,
    loadingMessage: '',
    loadingMessage3: '',
    isWindow: true,
    isWindow2: true,
    isSmartPhone: false,
    isTransparent: true,
    shpPropertieName: '',
    ocrAccuracy: 1,
    pmtilesName: '',
    pmtilesPropertieName: '',
    address: '',
    addressNoPref: '',
    addressYomi: '', // 住所の読み仮名（カタカナ）
    mapillaryFlg: false,
    streetViewFlg: false,
    streetViewCoords: null, // {lat, lng}
    streetViewLoading: false,
    nearbyPlacesFlg: false,
    nearbyPlacesData: null, // [{name, address, lat, lng, type, icon, typeLabel, distance}]
    nearbyPlacesLoading: false,
    nearbyPlacesMarkers: [], // maplibregl.Marker[]
    cad: '',
    //------------------------
    highwayYear: {
      map01: 2024,
      map02: 2024,
    },
    tetsudojikeiretsuYear: {
      map01: 2024,
      map02: 2024,
    },
    syochiikiNameText: {
      map01: '',
      map02: '',
    },
    // 2020町丁・字等別境界 (oh-syochiiki2) 用
    syochiiki2LineWidth: {
      map01: 2,
      map02: 2,
    },
    syochiiki2LineColor: {
      map01: '#000000',
      map02: '#000000',
    },
    bakumatsuText: {
      map01: '',
      map02: '',
    },
    bakumatsuText3d: {
      map01: '',
      map02: '',
    },
    bakumatsuSelected: {
      map01: '藩で色分け',
      map02: '藩で色分け',
    },
    koazaText: {
      map01: '',
      map02: '',
    },
    sekibutsuText: {
      map01: '',
      map02: '',
    },
    denshoText: {
      map01: '',
      map02: '',
    },
    mountainText: {
      map01: '',
      map02: '',
    },
    nadareText: {
      map01: '',
      map02: '',
    },
    bunkazaiText: {
      map01: '',
      map02: '',
    },
    tochiriyoActiveTypes: {
      map01: [],
      map02: [],
    },
    tochiriyoTokyoActiveTypes: {
      map01: [],
      map02: [],
    },
    yubinkuText: {
      map01: '',
      map02: '',
    },
    nrctPoiText: {
      map01: '',
      map02: '',
    },
    nrctMctText: {
      map01: '',
      map02: '',
    },
    schoolSelectedTypes: {
      map01: [],
      map02: [],
    },
    busSelected: {
      map01: '標準',
      map02: '標準',
    },
    tetsudoSelected: {
      map01: '[]',
      map02: '[]',
    },
    color: {
      map01: {
        sp28: { brightnessMin: 0, brightnessMax: 1, hueRotate: 0, contrast: 0, saturation: 0, sharpness: 0 },
        sp36: { brightnessMin: 0, brightnessMax: 1, hueRotate: 0, contrast: 0, saturation: 0, sharpness: 0 },
        sp45: { brightnessMin: 0, brightnessMax: 1, hueRotate: 0, contrast: 0, saturation: 0, sharpness: 0 },
        sp61: { brightnessMin: 0, brightnessMax: 1, hueRotate: 0, contrast: 0, saturation: 0, sharpness: 0 },
        sp74: { brightnessMin: 0, brightnessMax: 1, hueRotate: 0, contrast: 0, saturation: 0, sharpness: 0 },
        sp79: { brightnessMin: 0, brightnessMax: 1, hueRotate: 0, contrast: 0, saturation: 0, sharpness: 0 },
        sp84: { brightnessMin: 0, brightnessMax: 1, hueRotate: 0, contrast: 0, saturation: 0, sharpness: 0 },
        sp87: { brightnessMin: 0, brightnessMax: 1, hueRotate: 0, contrast: 0, saturation: 0, sharpness: 0 },
        jinsoku: { brightnessMin: 0, brightnessMax: 1, hueRotate: 0, contrast: 0, saturation: 0, sharpness: 0 },
      },
      map02: {
        sp28: { brightnessMin: 0, brightnessMax: 1, hueRotate: 0, contrast: 0, saturation: 0, sharpness: 0 },
        sp36: { brightnessMin: 0, brightnessMax: 1, hueRotate: 0, contrast: 0, saturation: 0, sharpness: 0 },
        sp45: { brightnessMin: 0, brightnessMax: 1, hueRotate: 0, contrast: 0, saturation: 0, sharpness: 0 },
        sp61: { brightnessMin: 0, brightnessMax: 1, hueRotate: 0, contrast: 0, saturation: 0, sharpness: 0 },
        sp74: { brightnessMin: 0, brightnessMax: 1, hueRotate: 0, contrast: 0, saturation: 0, sharpness: 0 },
        sp79: { brightnessMin: 0, brightnessMax: 1, hueRotate: 0, contrast: 0, saturation: 0, sharpness: 0 },
        sp84: { brightnessMin: 0, brightnessMax: 1, hueRotate: 0, contrast: 0, saturation: 0, sharpness: 0 },
        sp87: { brightnessMin: 0, brightnessMax: 1, hueRotate: 0, contrast: 0, saturation: 0, sharpness: 0 },
        jinsoku: { brightnessMin: 0, brightnessMax: 1, hueRotate: 0, contrast: 0, saturation: 0, sharpness: 0 },
      },
    },
    isPaintGeopark: {
      map01: true,
      map02: true,
    },
    kyakusuYear: {
      map01: 2022,
      map02: 2022,
    },
    jigyousya: {
      map01: '全て',
      map02: '全て',
    },
    isPaintBunsuirei: {
      map01: true,
      map02: true,
    },
    isKasen: {
      map01: true,
      map02: true,
    },
    suikeiText: {
      map01: '',
      map02: '',
    },
    kasenCode: {
      map01: '',
      map02: '',
    },
    kasenMei: {
      map01: '',
      map02: '',
    },
    // 洪水浸水想定区域フィルタ
    kozuiFilterText: {
      map01: '',
      map02: '',
    },
    kozuiFillEnabled: {
      map01: true,
      map02: true,
    },
    kozuiSelectedDepths: {
      map01: [1, 2, 3, 4, 5, 6],
      map02: [1, 2, 3, 4, 5, 6],
    },
    kozuiColorMode: {
      map01: 'warm',
      map02: 'warm',
    },
    // ハザード域内人口分析
    hazardPopRankFilter: {
      map01: [1, 2, 3, 4, 5, 6],
      map02: [1, 2, 3, 4, 5, 6],
    },
    hazardPopMeshOpacity: {
      map01: 0.5,
      map02: 0.5,
    },
    // ハザード域内人口メッシュが表示中か（既存ポップアップ抑制に使用）
    hazardPopActive: {
      map01: false,
      map02: false,
    },
    // メッシュ人口分析が有効か（既存ポップアップ抑制に使用）
    meshPopActive: {
      map01: false,
      map02: false,
    },
    kozui3dSelectedDepths: {
      map01: [1, 2, 3, 4, 5, 6],
      map02: [1, 2, 3, 4, 5, 6],
    },
    kozui3dFilterText: {
      map01: '',
      map02: '',
    },
    kozui3dColorMode: {
      map01: 'warm',
      map02: 'warm',
    },
    tadankaiRainfall: {
      map01: 100,
      map02: 100,
    },
    tadankaiFillEnabled: {
      map01: true,
      map02: true,
    },
    tadankaiSelectedDepths: {
      map01: [1, 2, 3, 4, 5, 6],
      map02: [1, 2, 3, 4, 5, 6],
    },
    tadankaiColorMode: {
      map01: 'blue',
      map02: 'blue',
    },
    gyoseiHistoryYear: {
      map01: new Date().getFullYear(),
      map02: new Date().getFullYear(),
    },
    gyoseiHistoryFillEnabled: {
      map01: true,
      map02: true,
    },
    gyoseiHistorySpeed: {
      map01: 1000,
      map02: 1000,
    },
    // 行政界履歴ドロワー
    gyoseiHistoryDrawer: {
      open: false,
      mapName: 'map01',
      data: null, // { uniqueFeatures, selectedMunicipality, selectedYear, historyData, coordinates }
      marker: null, // クリック地点のマーカー
    },
    // URL共有用: 行政界履歴復元待ちの座標 [lng, lat]
    pendingGyoseiHistoryCoord: null,
    isPaintCity: {
      map01: {
        t09: true,
        s25: true,
        h12: true,
        r05: true,
        koiki: true,
        gun: true,
      },
      map02: {
        t09: true,
        s25: true,
        h12: true,
        r05: true,
        koiki: true,
        gun: true,
      },
      map03: {
        t09: true,
        s25: true,
        h12: true,
        r05: true,
        koiki: true,
        gun: true,
      },
      map04: {
        t09: true,
        s25: true,
        h12: true,
        r05: true,
        koiki: true,
        gun: true,
      },
    },
    cityText: {
      map01: {
        t09: '',
        s25: '',
        h12: '',
        r05: '',
        koiki: '',
        gun: '',
      },
      map02: {
        t09: '',
        s25: '',
        h12: '',
        r05: '',
        koiki: '',
        gun: '',
      },
      map03: {
        t09: '',
        s25: '',
        h12: '',
        r05: '',
        koiki: '',
        gun: '',
      },
      map04: {
        t09: '',
        s25: '',
        h12: '',
        r05: '',
        koiki: '',
        gun: '',
      },
    },
    cityLineColor: {
      map01: {
        t09: '#000000',
        s25: '#000000',
        h12: '#000000',
        r05: '#000000',
        koiki: '#000000',
        gun: '#000000',
      },
      map02: {
        t09: '#000000',
        s25: '#000000',
        h12: '#000000',
        r05: '#000000',
        koiki: '#000000',
        gun: '#000000',
      },
      map03: {
        t09: '#000000',
        s25: '#000000',
        h12: '#000000',
        r05: '#000000',
        koiki: '#000000',
        gun: '#000000',
      },
      map04: {
        t09: '#000000',
        s25: '#000000',
        h12: '#000000',
        r05: '#000000',
        koiki: '#000000',
        gun: '#000000',
      },
    },
    cityLineWidth: {
      map01: {
        t09: 2,
        s25: 2,
        h12: 2,
        r05: 2,
        koiki: 2,
        gun: 2,
      },
      map02: {
        t09: 2,
        s25: 2,
        h12: 2,
        r05: 2,
        koiki: 2,
        gun: 2,
      },
      map03: {
        t09: 2,
        s25: 2,
        h12: 2,
        r05: 2,
        koiki: 2,
        gun: 2,
      },
      map04: {
        t09: 2,
        s25: 2,
        h12: 2,
        r05: 2,
        koiki: 2,
        gun: 2,
      },
    },
    osmText: {
      map01: '',
      map02: '',
    },
    rawQueryText: {
      map01: '',
      map02: '',
    },
    tokijyoText: {
      map01: '',
      map02: '',
    },
    tokijyoLineWidth: {
      map01: null,
      map02: null,
    },
    tokijyoColor: {
      map01: 'red',
      map02: 'red',
    },
    tokijyoCircleColor: {
      map01: 'red',
      map02: 'red',
    },
    seidoColorMode: {
      map01: false,
      map02: false,
    },
    bunpitsuDepthMode: {
      map01: false,
      map02: false,
    },
    bunpitsuFilter: {
      map01: null,
      map02: null,
    },
    hikkaiMiteichiFilter: {
      map01: false,
      map02: false,
    },
    mizuDouroColorMode: {
      map01: false,
      map02: false,
    },
    chibanText: {
      map01: '',
      map02: '',
    },
    chibanColor: {
      map01: 'blue',
      map02: 'blue',
    },
    chibanWidhs: [],
    chibanWidhsString: {
      map01: '',
      map02: '',
    },
    chibanColors: [],
    chibanColorsString: {
      map01: '',
      map02: '',
    },
    chibanCircleColor: {
      map01: 'rgba(0,0,0,0)',
      map02: 'rgba(0,0,0,0)',
    },
    // 赤色立体地図 / CS立体図のパラメーター
    mpiRrimMapMode: {
      map01: 0,
      map02: 0,
    },
    mpiRrimDebugMode: {
      map01: 0,
      map02: 0,
    },
    mpiRrimOpacity: {
      map01: 0.95,
      map02: 0.95,
    },
    // 赤色立体図用パラメーター
    mpiRrimSlopeMaxDeg: {
      map01: 90,
      map02: 90,
    },
    mpiRrimSaturationMax: {
      map01: 0.9,
      map02: 0.9,
    },
    mpiRrimValueMin: {
      map01: 0.1,
      map02: 0.1,
    },
    // CS立体図用パラメーター
    mpiCsSlopeMaxDeg: {
      map01: 60,
      map02: 60,
    },
    mpiCsCurvatureCoef: {
      map01: 0.01,
      map02: 0.01,
    },
    mpiCsValueMin: {
      map01: 0.1,
      map02: 0.1,
    },
    // 災害リスク分析用パラメーター（学術的根拠に基づく実装）
    disasterSlopeThreshold: {
      map01: 30,
      map02: 30,
    },
    disasterConcavityThreshold: {
      map01: -0.02,
      map02: -0.02,
    },
    disasterCurvatureAnomalyThreshold: {
      map01: 0.05,
      map02: 0.05,
    },
    disasterLandslideMinSlope: {
      map01: 10,
      map02: 10,
    },
    disasterFloodWaterLevel: {
      map01: 3.0,
      map02: 3.0,
    },
    disasterFloodBaseElevation: {
      map01: 20.0,
      map02: 20.0,
    },
    // 考古学的遺跡探索用パラメーター（学術的根拠に基づく実装）
    archeologyCircularityThreshold: {
      map01: 0.8,
      map02: 0.8,
    },
    archeologyFlatnessThreshold: {
      map01: 10,
      map02: 10,
    },
    archeologyRidgeMinSlope: {
      map01: 5,
      map02: 5,
    },
    archeologyRidgeMaxSlope: {
      map01: 25,
      map02: 25,
    },
    archeologyRidgeIntensity: {
      map01: 0.02,
      map02: 0.02,
    },
    archeologyChannelDepth: {
      map01: -0.03,
      map02: -0.03,
    },
    archeologyChannelContinuity: {
      map01: 0.7,
      map02: 0.7,
    },
    archeologyTerraceStepHeight: {
      map01: 1.5,
      map02: 1.5,
    },
    archeologyTerraceMinSlope: {
      map01: 10,
      map02: 10,
    },
    archeologyTerraceMaxSlope: {
      map01: 45,
      map02: 45,
    },
    archeologyMicroReliefBoost: {
      map01: 10,
      map02: 10,
    },
    archeologyCurvatureSensitivity: {
      map01: 0.001,
      map02: 0.001,
    },
    zeniKm: {
      map01: 20,
      map02: 20,
    },
    ntripKm: {
      map01: 20,
      map02: 20,
    },
    mindenKm: {
      map01: 20,
      map02: 20,
    },
    simaData: {
      map01: '',
      map02: '',
    },
    simaZahyokei: {
      map01: '',
      map02: '',
    },
    selectedkijyuntenItems: {
      map01: '',
      map02: '',
    },
    selectedGsikuItems: {
      map01: '',
      map02: '',
    },
    isDialogVisible: false,
    geojsonSources: [],
    // Welcome Wizard（初回起動ガイド）
    welcomeWizard: {
      open: false,
      completed: false,
      currentStep: 0,
      selectedPurpose: null, // 'survey' | 'draw' | 'view' | 'measure'
      totalSteps: 6, // 0:ようこそ, 1:用途選択, 2:機能紹介, 3:基本操作, 4:レイヤー, 5:完了
    },
    //------------------------
    // ヘルプシステム
    helpDialog: false, // ヘルプダイアログの表示状態
    helpInitialTopic: null, // 初期表示するトピックID
    helpSearchHistory: [], // 検索履歴
    helpViewHistory: [], // 閲覧履歴（最近見たトピック）
    // マルチインスタンスFW管理（OH3の作法）
    multiInstanceFWs: {
      'ai-point-info': [], // 瞬間レポート
      'property-report': [], // 物件調査レポート
      'ai-childcare-report': [], // 子育てレポート
      'super-population': [], // 超人口データ
      'area-analysis': [], // AI防災リスク診断
      'csv-join-analysis': [], // CSV結合分析
      'fs-investigation': [], // 土地法規等調査
      'jusetsu-draft': [], // 重説補助資料
      'maisoku': [], // 簡易マイソク
      'super-cross-section': [], // 超串刺し抽出
      'municipality-pyramid': [], // 市区町村別人口ピラミッド
      'chart-window': [], // マップAI: 統計D3チャート FW（複数インスタンス可）
    },
    lastDrawnPolygon: null, // 最後に描画されたポリゴン（AI防災リスク診断用）
  },
  getters: {
    // 元に戻す/やり直しの状態管理
    canUndo: (state) => {
      // マイドローモードの場合はマイドロー用Undoスタックをチェック
      if (state.isUsingServerGeojson) {
        return state.myDrawUndoCount > 0
      }
      return state.historyCursor > 0
    },
    canRedo: (state) => {
      // マイドローモードの場合はマイドロー用Redoスタックをチェック
      if (state.isUsingServerGeojson) {
        return state.myDrawRedoCount > 0
      }
      return state.historyCursor < state.prevGeojsons.length - 1
    },
    undoCount: (state) => {
      // マイドローモードの場合はマイドロー用カウントを返す
      if (state.isUsingServerGeojson) {
        return state.myDrawUndoCount
      }
      // 初期スナップショットは戻せる履歴としてカウントしないため-1する
      return Math.max(0, state.historyCursor - 1)
    },
    redoCount: (state) => {
      // マイドローモードの場合はマイドロー用カウントを返す
      if (state.isUsingServerGeojson) {
        return state.myDrawRedoCount
      }
      return Math.max(0, state.prevGeojsons.length - 1 - state.historyCursor)
    },
    historyInfo: (state, getters) => {
      return {
        canUndo: getters.canUndo,
        canRedo: getters.canRedo,
        undoCount: getters.undoCount,
        redoCount: getters.redoCount,
        totalHistory: state.prevGeojsons.length,
      }
    },
    // typeIndexで既存インスタンスを検索（地物連動用）
    findFWInstanceByTypeIndex: (state) => (type, typeIndex) => {
      const instances = state.multiInstanceFWs[type]
      if (!instances) return null
      return instances.find(i => i.typeIndex === typeIndex)
    },
  },
  mutations: {
    setShowPcMode(state, val) {
      state.showPcMode = val
    },
    // プランタブを開く
    openPlanTab(state) {
      state.openPlanTab = Date.now()
    },
    // マルチインスタンスFW管理（OH3の作法）
    addFWInstance(state, { type, data }) {
      const instances = state.multiInstanceFWs[type]
      if (!instances) return
      const id = Date.now()
      // dataにtypeIndexが指定されていればそれを使用（地物から復元時）、なければ新規連番
      const typeIndex = data.typeIndex !== undefined ? data.typeIndex : instances.length
      // AIレポート系は3種類で統一したオフセットを使用（FWが重ならないように）
      const aiReportTypes = ['ai-point-info', 'property-report', 'ai-childcare-report', 'super-population', 'area-analysis', 'fs-investigation', 'jusetsu-draft', 'maisoku']
      let index
      if (aiReportTypes.includes(type)) {
        // 全AIレポートの合計数をインデックスとして使用（FWずらし用）
        index = aiReportTypes.reduce((sum, t) => sum + (state.multiInstanceFWs[t]?.length || 0), 0)
      } else {
        index = instances.length
      }
      instances.push({ id, index, typeIndex, visible: true, ...data })
      // ※ floatingWindows[windowId]はコンポーネントのmounted()でsetFloatingVisibleする（既存方式に合わせる）
    },
    removeFWInstance(state, { type, id }) {
      const instances = state.multiInstanceFWs[type]
      if (!instances) return
      const idx = instances.findIndex(i => i.id === id)
      if (idx !== -1) instances.splice(idx, 1)
      // インデックス再計算（ずらし位置調整用）
      instances.forEach((inst, i) => inst.index = i)
    },
    // FWを非表示にする（データは保持）
    hideFWInstance(state, { type, id }) {
      const instances = state.multiInstanceFWs[type]
      if (!instances) return
      const inst = instances.find(i => i.id === id)
      if (inst) inst.visible = false
    },
    // 非表示のFWを再表示
    showFWInstance(state, { type, id }) {
      const instances = state.multiInstanceFWs[type]
      if (!instances) return
      const inst = instances.find(i => i.id === id)
      if (inst) inst.visible = true
    },
    setDrawButtonsVisible(state, value) {
      state.drawButtonsVisible = value
    },
    setCurrentDrawFeature(state, feature) {
      // 地物が切り替わったらDrawDrawerを閉じる
      // ただしfeatureがPoint（テキスト・円中心除く）の場合は閉じない（直後にsetDrawDrawerで開くため）
      const isPointKeepOpen = feature &&
        feature.geometry?.type === 'Point' &&
        !feature.properties?.isCircleCenter &&
        feature.properties?.type !== 'Text'
      if (!isPointKeepOpen) {
        state.showDrawDrawer = false
      }
      state.currentDrawFeature = feature
    },
    // お気に入りレイヤーの追加/削除（同期的な更新のみ）
    TOGGLE_FAVORITE_LAYER(state, layerId) {
      const index = state.favoriteLayerIds.indexOf(layerId)
      if (index > -1) {
        state.favoriteLayerIds.splice(index, 1)
      } else {
        // 新しいお気に入りは先頭に追加
        state.favoriteLayerIds.unshift(layerId)
      }
    },
    SET_FAVORITE_LAYERS(state, layers) {
      state.favoriteLayerIds = layers
    },
    // お気に入りレイヤーの並び順を更新
    UPDATE_FAVORITE_LAYER_ORDER(state, orderedIds) {
      state.favoriteLayerIds = orderedIds
    },
    // 最近使用したレイヤーの追加（同期的な更新のみ）
    ADD_RECENT_LAYER(state, layerId) {
      if (!layerId) return
      // 既存の位置から削除
      const filtered = state.recentLayerIds.filter((id) => id !== layerId)
      // 先頭に追加（件数制限なし）
      state.recentLayerIds = [layerId, ...filtered]
    },
    SET_RECENT_LAYERS(state, layers) {
      state.recentLayerIds = layers
    },
    // 緑のオープンデータ：カテゴリフィルター更新
    setGreenSelectedCategories(state, categoryId) {
      state.greenSelectedCategory = categoryId
    },
    SET_HS_ENABLED(state, v) {
      state.hillshade.enabled = !!v
      state.updatePermalinkFire = !state.updatePermalinkFire
    },
    SET_HS_FOR(state, { mapKey, enabled }) {
      state.hillshade.maps[mapKey] = !!enabled
      state.updatePermalinkFire = !state.updatePermalinkFire
    },
    HS_SET_PARAM(state, { key, value }) {
      state.hillshade.params[key] = value
      state.updatePermalinkFire = !state.updatePermalinkFire
    },
    HS_MD_SET_AT(state, { kind, index, value }) {
      const arr = state.hillshade.params[kind]
      if (!Array.isArray(arr)) return
      arr.splice(index, 1, value)
      state.updatePermalinkFire = !state.updatePermalinkFire
    },
    HS_MD_ADD(state) {
      const p = state.hillshade.params
      p.mdDirections.push(45)
      p.mdAltitudes.push(25)
      p.mdHighlights.push('#eef8ff')
      p.mdShadows.push('#2a2a6e')
      state.updatePermalinkFire = !state.updatePermalinkFire
    },
    HS_MD_REMOVE(state, index) {
      const p = state.hillshade.params
      const n = Math.min(p.mdDirections.length, p.mdAltitudes.length, p.mdHighlights.length, p.mdShadows.length)
      if (n <= 1) return
      p.mdDirections.splice(index, 1)
      p.mdAltitudes.splice(index, 1)
      p.mdHighlights.splice(index, 1)
      p.mdShadows.splice(index, 1)
      state.updatePermalinkFire = !state.updatePermalinkFire
    },
    HS_RESET(state) {
      state.hillshade = defaults()
      state.updatePermalinkFire = !state.updatePermalinkFire
    },
    // 不動産調査レポート
    setShowRealEstateReportDialog(state, value) {
      state.showRealEstateReportDialog = value
    },
    setRealEstateReportLocation(state, location) {
      state.realEstateReportLocation = location
    },
    setCurrentLocation(state, location) {
      state.currentLocation = location
    },
    setCurrentLocationMarker(state, marker) {
      state.currentLocationMarker = marker
    },
    // オンボーディング関連
    START_ONBOARDING(state) {
      state.onboarding.active = true
      state.onboarding.currentStep = 0
      state.onboarding.completed = false
    },
    NEXT_ONBOARDING_STEP(state) {
      state.onboarding.currentStep++
    },
    COMPLETE_ONBOARDING(state) {
      state.onboarding.active = false
      state.onboarding.completed = true
      try {
        localStorage.setItem('oh3_onboarding_completed', 'true')
      } catch (e) {
        console.warn('localStorage not available:', e)
      }
    },
    SKIP_ONBOARDING(state) {
      state.onboarding.active = false
      try {
        localStorage.setItem('oh3_onboarding_completed', 'true')
      } catch (e) {
        console.warn('localStorage not available:', e)
      }
    },
    setLevel(s, v) {
      s.level = Number(v) || 0
    },
    setMode(s, m) {
      s.mode = m === 'linear' ? 'linear' : 'step'
    },
    setPalette(s, p) {
      s.palette = p
    },
    // 一気に false にする（固定リスト版）
    disableAllDraws(state) {
      state.isDrawLasso = false
      state.isDrawFix = false
      state.isDrawFree = false
      state.isDrawPolygon = false
      state.isDrawLine = false
      state.isDrawCircle = false
      state.isDrawPoint = false
      state.isDrawText = false
      // document.querySelector('#floating-buttons').style.display = 'none';
      state.showDrawConfrim = false
    },
    // どれか1つだけ true にしたいとき用（要改修）
    // 使用例: commit('SET_DRAW_MODE', 'isDrawPolygon')
    SET_DRAW_MODE(state, key) {
      Object.keys(state).forEach((k) => {
        if (k.startsWith('isDraw') && k !== 'isDraw') state[k] = false
      })
      if (key && key in state) state[key] = true
    },
    setFloatingVisible(state, { id, visible }) {
      state.floatingWindows = {
        ...state.floatingWindows,
        [id]: visible,
      }
    },
    setIsochronePreset(state, preset) {
      state.isochronePreset = preset
    },
    setRoadIsochronePreset(state, preset) {
      state.roadIsochronePreset = preset
    },
    setContextHelpContext (state, ctx) {
      state.contextHelpContext = ctx // { title, page } or null
    },
    setPhotoSketchTarget(state, target) {
      state.photoSketchTarget = target
    },
    setTextDetailTarget(state, target) {
      state.textDetailTarget = target
    },
    setBottomSheetActive(state, active) {
      state.bottomSheetActive = active
    },
    // 編集中の地物ID（地物単位の変形・移動用）
    setEditingFeatureId(state, featureId) {
      state.editingFeatureId = featureId
      // 互換性のためeditEnabledも同期
      state.editEnabled = (featureId !== null)
    },
    // グローバル画像プレビューを開く
    openGlobalMediaPreview(state, { src, kind = 'image', filename = '' }) {
      state.globalMediaPreview = { open: true, src, kind, filename }
    },
    // グローバル画像プレビューを閉じる
    closeGlobalMediaPreview(state) {
      state.globalMediaPreview = { open: false, src: '', kind: 'image', filename: '' }
    },
    setKyusekiPreview(state, payload = {}) {
      state.kyusekiPreview = {
        ...state.kyusekiPreview,
        ...payload,
        timestamp: Date.now(),
      }
    },
    resetKyusekiPreview(state) {
      state.kyusekiPreview = defaultKyusekiPreviewState()
    },
    // 旧版地形図プレビュー（無限FW）
    addMw5Preview(state, { id, title, extent }) {
      // 同じIDが既に開いていたら追加しない
      if (state.mw5Previews.some(p => p.id === id)) return
      state.mw5Previews = [...state.mw5Previews, { id, title, extent }]
      state.floatingWindows = { ...state.floatingWindows, ['mw5-preview-' + id]: true }
    },
    removeMw5Preview(state, id) {
      state.mw5Previews = state.mw5Previews.filter(p => p.id !== id)
      const fw = { ...state.floatingWindows }
      delete fw['mw5-preview-' + id]
      state.floatingWindows = fw
    },
    // shareFeatures関連
    addShareFeature(state, feature) {
      const id = `share-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
      // 同タイプ内の最大typeIndexを取得して+1（リロード後も正しい番号を振るため）
      const sameTypeFeatures = state.shareFeatures.filter(f => f.aiType === feature.aiType)
      const maxTypeIndex = sameTypeFeatures.length > 0
        ? Math.max(...sameTypeFeatures.map(f => f.typeIndex ?? -1))
        : -1
      const newTypeIndex = maxTypeIndex + 1
      const newFeature = {
        id,
        aiType: feature.aiType,
        geometry: feature.geometry,
        typeIndex: newTypeIndex, // 同タイプ内のインデックス（ナンバリング用）
        properties: { ...feature.properties, aiType: feature.aiType, id, typeIndex: newTypeIndex }
      }
      // 配列を新しく作り直してリアクティビティを確保
      state.shareFeatures = [...state.shareFeatures, newFeature]
      state.updatePermalinkFire = !state.updatePermalinkFire
    },
    removeShareFeature(state, id) {
      state.shareFeatures = state.shareFeatures.filter(f => f.id !== id)
      state.updatePermalinkFire = !state.updatePermalinkFire
    },
    updateShareFeatureProperties(state, { id, properties }) {
      // 特定 feature の properties をマージ更新 (リアクティブ)
      state.shareFeatures = state.shareFeatures.map(f => {
        if (f.id !== id) return f
        return { ...f, properties: { ...f.properties, ...properties } }
      })
      state.updatePermalinkFire = !state.updatePermalinkFire
    },
    setShareFeatures(state, features) {
      state.shareFeatures = features
    },
    setShareFeaturesLayerAdded(state, value) {
      state.shareFeaturesLayerAdded = value
    },
    setHighlightedRankingPointId(state, pointId) {
      state.highlightedRankingPointId = pointId
    },
    setTiffAndWorldFile(state, files) {
      state.tiffAndWorldFile = files
    },
    setCurrentGroupLayers(state, layers) {
      state.currentGroupLayers = layers
    },
    setGroupFeatures(state, features) {
      state.groupFeatures = features
    },
    updateSelectedPointPhotoUrl(state, photoUrl) {
      // 新しいミューテーション
      if (state.selectedPointFeature) {
        state.selectedPointFeature.properties.photoUrl = photoUrl
      }
    },
    setGroupGeojsonFeatures(state, features) {
      state.groupGeojson.features = features
    },
    setSnackbarForGroup(state, value) {
      state.snackbarForGroup = value
    },
    setFileDropFlowAction(state, payload) {
      state.fileDropFlow.action = payload.action
      state.fileDropFlow.files = payload.files
    },
    openFileDropFlow(state, payload = {}) {
      const { files = [], mode = 'user-select' } = payload
      state.fileDropFlow.open = true
      state.fileDropFlow.files = files
      state.fileDropFlow.action = null
      state.fileDropFlow.mode = mode
    },
    closeFileDropFlow(state) {
      state.fileDropFlow.open = false
      state.fileDropFlow.files = []
      state.fileDropFlow.action = null
    },
    setSelectedLayerId(state, layerId) {
      state.selectedLayerId = layerId
    },
    hideSnackbarForGroup(state) {
      state.snackbarForGroup = false
      state.snackbarForGroupText = ''
    },
    setCurrentPointLayerId(state, id) {
      state.currentPointLayerId = id
    },
    addPointLayer(state) {
      const id = 'layer' + (state.pointLayers.length + 1)
      state.pointLayers.push({ id, name: `ポイントレイヤー${state.pointLayers.length + 1}` })
    },
    removePointLayer(state, id) {
      const index = state.pointLayers.findIndex((l) => l.id === id)
      if (index !== -1) {
        state.pointLayers.splice(index, 1)
        if (state.currentPointLayerIndex >= state.pointLayers.length) {
          state.currentPointLayerIndex = Math.max(0, state.pointLayers.length - 1)
        }
      }
    },
    setCurrentPointLayerIndex(state, index) {
      state.currentPointLayerIndex = index
    },
    // -----------------------------------------------------------------------------------------------------------------
    removePointFeature(state, id) {
      const index = state.groupGeojson.features.findIndex((f) => f.properties?.id === id)
      // alert(index);
      if (index !== -1) {
        state.groupGeojson.features.splice(index, 1)
      }
    },
    // 他のミューテーションがある中に追加
    setGroupGeojson(state, geojson) {
      state.groupGeojson = geojson
    },
    saveSelectedPointFeature(state) {
      const feature = state.selectedPointFeature
      if (!feature || !feature.properties?.id) return


      const features = state.groupGeojson.features
      const index = features.findIndex((f) => f.properties?.id === feature.properties.id)

      if (index !== -1) {
        // 既存のポイントを更新
        state.groupGeojson.features[index] = JSON.parse(JSON.stringify(feature))
      } else {
        // 新しいポイントを追加
        state.groupGeojson.features.push(JSON.parse(JSON.stringify(feature)))
      }
    },
    setDrawListDrawer(state, val) {
      state.showDrawListDrawer = val
    },
    setDrawDrawer(state, val) {
      // alert('store!' + val)
      if (process.env.NODE_ENV !== 'production') {
        console.groupCollapsed('%c[TRACE] setDrawDrawer', 'font-weight:bold')
        console.trace() // ← これで呼び出し元スタックが出る
        console.groupEnd()
        // 必要なら:
        // debugger; // デバッガでその場停止して call stack を見る
      }
      state.showDrawDrawer = val
    },
    setDrawDuplicate(state, val) {
      state.isDrawDuplicate = val
    },
    setDuplicateSourceFeature(state, feature) {
      state.duplicateSourceFeature = feature
    },
    setPointInfoDrawer(state, val) {
      if (state.isDraw || state.elevationDrawMode) return
      state.showPointInfoDrawer = val
    },
    setRightDrawer(state, val) {
      if (state.isDraw || state.elevationDrawMode) return
      state.showRightDrawer = val
    },
    setChibanzuDrawer(state, val) {
      if (state.isDraw || state.elevationDrawMode) return
      state.showChibanzuDrawer = val
    },
    // 行政界履歴ドロワー
    setGyoseiHistoryDrawer(state, { open, mapName, data, marker }) {
      if (open !== undefined) state.gyoseiHistoryDrawer.open = open
      if (mapName !== undefined) state.gyoseiHistoryDrawer.mapName = mapName
      if (data !== undefined) state.gyoseiHistoryDrawer.data = data
      if (marker !== undefined) state.gyoseiHistoryDrawer.marker = marker
    },
    setAboutDrawer(state, val) {
      state.showAboutDrawer = val
    },
    setSelectedPointFeature(state, feature) {
      state.selectedPointFeature = feature
    },
    updateSelectedPointTitle(state, title) {
      // 新しいミューテーション
      if (state.selectedPointFeature) {
        state.selectedPointFeature.properties.title = title
      }
    },
    updateSelectedPointDescription(state, val) {
      if (state.selectedPointFeature) {
        state.selectedPointFeature.properties.description = val
      }
    },
    updatePointDescription(state, { id, description }) {
      const feature = state.groupGeojson.features.find((f) => f.properties.id === id)
      if (feature) {
        feature.properties.description = description
      }
    },
    showSnackbarForGroup(state, text) {
      state.snackbarForGroupText = text
      state.snackbarForGroup = true
    },
    hideSnackbar(state) {
      state.snackbarForGroup = false
    },
    setCurrentGroupId(state, name) {
      state.currentGroupId = name
    },
    setCurrentGroupName(state, name) {
      state.currentGroupName = name
    },
    setUserId(state, uid) {
      state.userId = uid
    },
    SET_SEARCH_HISTORY(state, history) {
      const arr = Array.isArray(history) ? history : []
      state.searchHistory = arr.slice(0, 100)
    },
    ADD_SEARCH_HISTORY_TERM(state, payload) {
      // payload: { term, lat, lng } または 文字列(後方互換)
      let entry
      if (typeof payload === 'string') {
        const t = payload.trim()
        if (!t) return
        entry = { term: t }
      } else {
        const t = (payload.term || '').trim()
        if (!t) return
        entry = { term: t, lat: payload.lat, lng: payload.lng }
      }
      // 同じtermは削除（オブジェクト/文字列両対応）
      const filtered = state.searchHistory.filter((item) => {
        const itemTerm = typeof item === 'string' ? item : item.term
        return itemTerm !== entry.term
      })
      filtered.unshift(entry)
      state.searchHistory = filtered.slice(0, 100)
    },
    REMOVE_SEARCH_HISTORY_TERM(state, term) {
      const t = (term || '').trim()
      if (!t) return
      state.searchHistory = state.searchHistory.filter((item) => {
        const itemTerm = typeof item === 'string' ? item : item.term
        return itemTerm !== t
      })
    },
    // お気に入り地点
    SET_FAVORITE_LOCATIONS(state, locations) {
      state.favoriteLocations = Array.isArray(locations) ? locations.slice(0, 50) : []
    },
    ADD_FAVORITE_LOCATION(state, location) {
      if (!location || !location.name) return
      // 同名は上書き
      const filtered = state.favoriteLocations.filter((item) => item.name !== location.name)
      filtered.unshift(location)
      state.favoriteLocations = filtered.slice(0, 50)
    },
    UPDATE_FAVORITE_LOCATION(state, { oldName, newLocation }) {
      const index = state.favoriteLocations.findIndex((item) => item.name === oldName)
      if (index >= 0) {
        state.favoriteLocations.splice(index, 1, newLocation)
      }
    },
    REMOVE_FAVORITE_LOCATION(state, name) {
      state.favoriteLocations = state.favoriteLocations.filter((item) => item.name !== name)
    },
    // 保存マーカー
    SET_SAVED_MARKERS(state, markers) {
      state.savedMarkers = Array.isArray(markers) ? markers : []
    },
    ADD_SAVED_MARKER(state, marker) {
      if (!marker || !marker.id) return
      state.savedMarkers.unshift(marker)
    },
    REMOVE_SAVED_MARKER(state, markerId) {
      state.savedMarkers = state.savedMarkers.filter((item) => item.id !== markerId)
    },
    updateParam(state, payload) {
      let variable
      let variable2
      const estSps = [
        'ext-sp28',
        'ext-sp36',
        'ext-sp45',
        'ext-sp61',
        'ext-sp74',
        'ext-sp79',
        'ext-sp84',
        'ext-sp87',
        'ext-jinsoku',
      ]
      if (estSps.includes(payload.name)) {
        const target = payload.name.split('-')[1]
        let target2
        switch (payload.order) {
          case 0:
            target2 = 'brightnessMin'
            break
          case 1:
            target2 = 'brightnessMax'
            break
          case 2:
            target2 = 'hueRotate'
            break
          case 3:
            target2 = 'contrast'
            break
          case 4:
            target2 = 'saturation'
            break
          // case 5:
          //   target2 = 'sharpness'
          //   break
        }
        state.color[payload.mapName][target][target2] = Number(payload.value)
      } else {
        switch (payload.name) {
          case 'extHighway':
            variable = 'highwayYear'
            break
          case 'extTetsudojikeiretsu':
            variable = 'tetsudojikeiretsuYear'
            break
          case 'extSyochiiki':
            variable = 'syochiikiNameText'
            break
          case 'ext-syochiiki2':
            if (payload.order === 0) {
              variable = 'syochiiki2LineWidth'
            } else if (payload.order === 1) {
              variable = 'syochiiki2LineColor'
            }
            break
          case 'extBakumatsu':
            if (payload.order === 0) {
              variable = 'bakumatsuText'
            } else if (payload.order === 1) {
              variable = 'bakumatsuSelected'
            }
            break
          case 'extBakumatsu3d':
            variable = 'bakumatsuText3d'
            break
          case 'jinko250m':
            if (payload.order === 0) {
              variable = 'jinko250m'
            } else if (payload.order === 1) {
              variable = 'paintCheck250m'
            }
            break
          case 'extBus':
            variable = 'busSelected'
            break
          case 'extTetsudo':
            variable = 'tetsudoSelected'
            break
          case 'extKoaza':
            variable = 'koazaText'
            break
          case 'extGeopark':
            variable = 'isPaintGeopark'
            break
          case 'extKyakusu':
            if (payload.order === 0) {
              variable = 'kyakusuYear'
            } else if (payload.order === 1) {
              variable = 'jigyousya'
            }
            break
          case 'extSekibutsu':
            variable = 'sekibutsuText'
            break
          case 'extDensho':
            variable = 'denshoText'
            break
          case 'extMountain':
            variable = 'mountainText'
            break
          case 'extNadare':
            variable = 'nadareText'
            break
          case 'extBunkazai':
            variable = 'bunkazaiText'
            break
          case 'extTochiriyo':
            variable = 'tochiriyoActiveTypes'
            break
          case 'extTochiriyoTokyo':
            variable = 'tochiriyoTokyoActiveTypes'
            break
          case 'extYubinku':
            return
          case 'extSchool':
            variable = 'schoolSelectedTypes'
            break
          case 'extBunsuirei':
            if (payload.order === 0) {
              variable = 'isPaintBunsuirei'
            } else if (payload.order === 1) {
              variable = 'isKasen'
            } else if (payload.order === 2) {
              variable = 'suikeiText'
            } else if (payload.order === 3) {
              variable = 'kasenCode'
            } else if (payload.order === 4) {
              variable = 'kasenMei'
            }
            break
          case 'extKozui':
            if (payload.order === 0) {
              variable = 'kozuiFillEnabled'
            } else if (payload.order === 1) {
              variable = 'kozuiFilterText'
            } else if (payload.order === 2) {
              variable = 'kozuiSelectedDepths'
            } else if (payload.order === 3) {
              variable = 'kozuiColorMode'
            }
            break
          case 'ext-tadankai':
            if (payload.order === 0) {
              variable = 'tadankaiRainfall'
            } else if (payload.order === 1) {
              variable = 'tadankaiFillEnabled'
            } else if (payload.order === 2) {
              variable = 'tadankaiSelectedDepths'
            } else if (payload.order === 3) {
              variable = 'tadankaiColorMode'
            }
            break
          case 'ext-gyosei-history':
            if (payload.order === 0) {
              variable = 'gyoseiHistoryYear'
            } else if (payload.order === 1) {
              variable = 'gyoseiHistoryFillEnabled'
            } else if (payload.order === 2) {
              variable = 'gyoseiHistorySpeed'
            }
            break
          case 'extKozui3d':
            if (payload.order === 0) {
              variable = 'kozui3dFilterText'
            } else if (payload.order === 1) {
              variable = 'kozui3dSelectedDepths'
            } else if (payload.order === 2) {
              variable = 'kozui3dColorMode'
            }
            break
          case 'ext-city-gun':
          case 'ext-city-r05':
          case 'ext-city-h12':
          case 'ext-city-s25':
          case 'ext-city-t09':
          case 'ext-city-koiki':
            if (payload.order === 0) {
              variable = 'isPaintCity'
              variable2 = payload.name.split('-')[2]
            } else if (payload.order === 1) {
              variable = 'cityText'
              variable2 = payload.name.split('-')[2]
            } else if (payload.order === 2) {
              variable = 'cityLineColor'
              variable2 = payload.name.split('-')[2]
            } else if (payload.order === 3) {
              variable = 'cityLineWidth'
              variable2 = payload.name.split('-')[2]
            }
            break
          case 'extOSM':
            if (payload.order === 0) {
              variable = 'osmText'
            }
            if (payload.order === 1) {
              variable = 'rawQueryText'
            }
            break
          case 'ext-chibanzu':
            if (payload.order === 0) {
              variable = 'chibanText'
            }
            if (payload.order === 1) {
              variable = 'chibanColorsString'
            }
            if (payload.order === 2) {
              variable = 'chibanCircleColor'
            }
            if (payload.order === 3) {
              variable = 'chibanWidhsString'
            }
            break
          case 'ext-mpi-rrim':
            if (payload.order === 0) {
              variable = 'mpiRrimMapMode'
            }
            if (payload.order === 1) {
              variable = 'mpiRrimDebugMode'
            }
            if (payload.order === 2) {
              variable = 'mpiRrimOpacity'
            }
            if (payload.order === 3) {
              variable = 'mpiRrimSlopeMaxDeg'
            }
            if (payload.order === 4) {
              variable = 'mpiRrimSaturationMax'
            }
            if (payload.order === 5) {
              variable = 'mpiRrimValueMin'
            }
            if (payload.order === 6) {
              variable = 'mpiCsSlopeMaxDeg'
            }
            if (payload.order === 7) {
              variable = 'mpiCsCurvatureCoef'
            }
            if (payload.order === 8) {
              variable = 'mpiCsValueMin'
            }
            if (payload.order === 9) {
              variable = 'disasterSlopeThreshold'
            }
            if (payload.order === 10) {
              variable = 'disasterConcavityThreshold'
            }
            if (payload.order === 11) {
              variable = 'disasterCurvatureAnomalyThreshold'
            }
            if (payload.order === 12) {
              variable = 'disasterLandslideMinSlope'
            }
            if (payload.order === 13) {
              variable = 'disasterFloodWaterLevel'
            }
            if (payload.order === 14) {
              variable = 'disasterFloodBaseElevation'
            }
            // 考古学的遺跡探索用パラメーター（学術的根拠に基づく実装）
            if (payload.order === 15) {
              variable = 'archeologyCircularityThreshold'
            }
            if (payload.order === 16) {
              variable = 'archeologyFlatnessThreshold'
            }
            if (payload.order === 17) {
              variable = 'archeologyRidgeMinSlope'
            }
            if (payload.order === 18) {
              variable = 'archeologyRidgeMaxSlope'
            }
            if (payload.order === 19) {
              variable = 'archeologyRidgeIntensity'
            }
            if (payload.order === 20) {
              variable = 'archeologyChannelDepth'
            }
            if (payload.order === 21) {
              variable = 'archeologyChannelContinuity'
            }
            if (payload.order === 22) {
              variable = 'archeologyTerraceStepHeight'
            }
            if (payload.order === 23) {
              variable = 'archeologyTerraceMinSlope'
            }
            if (payload.order === 24) {
              variable = 'archeologyTerraceMaxSlope'
            }
            if (payload.order === 25) {
              variable = 'archeologyMicroReliefBoost'
            }
            if (payload.order === 26) {
              variable = 'archeologyCurvatureSensitivity'
            }
            break
          case 'extZeni':
            if (payload.order === 0) {
              variable = 'zeniKm'
            }
            break
          case 'extNtrip':
            if (payload.order === 0) {
              variable = 'ntripKm'
            }
            break
          case 'extMinden':
            if (payload.order === 0) {
              variable = 'mindenKm'
            }
            break
          case 'extSeaLevel':
            if (payload.order === 0) {
              variable = 'seaLevel'
            } else if (payload.order === 1) {
              variable = 'seaLevelTheme'
            }
            break
          case 'extHypsometric':
            if (payload.order === 0) {
              variable = 'hypsoLevel'
            } else if (payload.order === 1) {
              variable = 'hypsoScheme'
            }
            break
          case 'extKhonkijyunten':
            if (payload.order === 0) {
              variable = 'selectedkijyuntenItems'
            }
            break
          case 'extGaiku':
            if (payload.order === 0) {
              variable = 'selectedGsikuItems'
            }
            break
          case 'extGsiPp':
            // GSI単写真フィルタ: フィルタはDialog-layer.vueでレイヤー追加時に適用
            // UIでの変更はext-gsi-pp.vueのapplyMapFilter()が直接処理
            // extFireトグル不要（無限ループ防止）
            return
        }
        // if (variable === 'chibanColorsString') alert(payload.order + '///' + payload.value)

        // variableが未定義の場合はエラーを出力してスキップ
        if (!variable) {
          console.error('[updateParam] variable is undefined:', payload)
          return
        }

        // stateに変数が存在しない場合はエラーを出力してスキップ
        if (!state[variable]) {
          console.error('[updateParam] state variable not found:', variable, payload)
          return
        }

        // 値が変更された場合のみ更新とextFireトグルを行う
        let valueChanged = false
        if (!variable2) {
          if (state[variable][payload.mapName] !== payload.value) {
            state[variable][payload.mapName] = payload.value
            valueChanged = true
          }
        } else {
          // alert()
          if (state[variable][payload.mapName][variable2] !== payload.value) {
            state[variable][payload.mapName][variable2] = payload.value
            valueChanged = true
          }
        }

        // extFire廃止: 各extコンポーネントのmounted()で初期適用するため、ここからの通知は不要
      }
    },
    updateSelectedLayers(state, payload) {
      const result = state.selectedLayers[payload.mapName].find((el) => el.id === payload.id)
      if (!result || !result.ext) {
        console.error('[updateSelectedLayers] レイヤーまたはextが見つかりません:', payload)
        return
      }
      // 注: addLayersRequestedはここで変更しない（ext側のパラメータ変更はaddLayersの再実行不要）
      result.ext.values = payload.values
    },
    incrDialogMaxZindex(state) {
      state.dialogMaxZindex++
    },
    incrDialog2Id(state) {
      state.dialog2Id++
    },
    // インフォ用ダイアログの追加------------------------------------------------------------------
    pushDialogsInfo(state, payload) {
      const dialogs = state.dialogsInfo[payload.mapName]
      dialogs.push(payload.dialog)
    },
    pushDialogs2(state, payload) {
      const dialogs = state.dialogs2[payload.mapName]
      dialogs.push(payload.dialog)
    },
    // Welcome Wizard mutations
    SET_WIZARD_OPEN(state, open) {
      state.welcomeWizard.open = !!open
    },
    SET_WIZARD_COMPLETED(state, completed) {
      state.welcomeWizard.completed = !!completed
    },
    SET_WIZARD_STEP(state, step) {
      state.welcomeWizard.currentStep = step
    },
    SET_WIZARD_PURPOSE(state, purpose) {
      state.welcomeWizard.selectedPurpose = purpose
    },
    RESET_WIZARD(state) {
      state.welcomeWizard.open = false
      state.welcomeWizard.currentStep = 0
      state.welcomeWizard.selectedPurpose = null
    },
    // ハザードレイヤー操作用mutations
    ADD_SELECTED_LAYER(state, { mapName, layer }) {
      const layers = state.selectedLayers[mapName]
      // 既に存在しないかチェック
      const exists = layers.some((l) => l.id === layer.id)
      if (!exists) {
        layers.unshift(layer)
      }
    },
    REMOVE_SELECTED_LAYER(state, { mapName, layerId }) {
      const layers = state.selectedLayers[mapName]
      const index = layers.findIndex((l) => l.id === layerId)
      if (index !== -1) {
        layers.splice(index, 1)
      }
    },
    TOGGLE_SELECTED_LAYER(state, { mapName, layer }) {
      const layers = state.selectedLayers[mapName]
      const index = layers.findIndex((l) => l.id === layer.id)

      if (index !== -1) {
        // 既に存在する場合は削除
        layers.splice(index, 1)
      } else {
        // 存在しない場合は追加（先頭に）
        layers.unshift(layer)
      }

    },

    // ヘルプシステム関連
    setHelpDialog(state, value) {
      state.helpDialog = value
    },

    setHelpTopic(state, topicId) {
      state.helpInitialTopic = topicId
    },

    addHelpSearchHistory(state, query) {
      if (!state.helpSearchHistory.includes(query)) {
        state.helpSearchHistory.unshift(query)
        // 最大10件まで保持
        if (state.helpSearchHistory.length > 10) {
          state.helpSearchHistory.pop()
        }
      }
    },

    addHelpViewHistory(state, topicId) {
      // 既存の履歴から削除して先頭に追加
      state.helpViewHistory = state.helpViewHistory.filter((id) => id !== topicId)
      state.helpViewHistory.unshift(topicId)
      // 最大20件まで保持
      if (state.helpViewHistory.length > 20) {
        state.helpViewHistory.pop()
      }
    },

    clearHelpHistory(state) {
      state.helpSearchHistory = []
      state.helpViewHistory = []
    },
    // --- App.vueの直接書き換え排除用（Day4） ---
    setShowTeacherDashboard(state, val) {
      state.showTeacherDashboard = val
    },
    setShowOrganizationAdmin(state, val) {
      state.showOrganizationAdmin = val
    },
    setOrganizationInfo(state, val) {
      state.organizationInfo = val
    },
    setIsOrganizationOwner(state, val) {
      state.isOrganizationOwner = val
    },
    setPrevGeojsons(state, val) {
      state.prevGeojsons = val
    },
    setLoadingMessage3(state, val) {
      state.loadingMessage3 = val
    },
    setTransparent(state, val) {
      state.transparent = val
    },
    setResolution(state, val) {
      state.resolution = val
    },
    setIsKyusekiDialogOpen(state, val) {
      state.isKyusekiDialogOpen = val
    },
    setTargetSeq(state, val) {
      state.targetSeq = val
    },
    setFilter360(state, val) {
      state.filter360 = val
    },
    setClickCircleGeojsonText(state, val) {
      state.clickCircleGeojsonText = val
    },
    toggleUpdatePermalinkFire(state) {
      state.updatePermalinkFire = !state.updatePermalinkFire
    },
    // --- downLoad / simaExport / dxfExport の直接書き換え排除用 ---
    setLoading(state, val) {
      state.loading = val
    },
    setLoading2(state, val) {
      state.loading2 = val
    },
    setLoading3(state, val) {
      state.loading3 = val
    },
    setLoadingMessage(state, val) {
      state.loadingMessage = val
    },
    setFetchImagesFire(state, val) {
      state.fetchImagesFire = val
    },
    setUploadedImage(state, val) {
      state.uploadedImage = val
    },
    setSuspendExternalSync(state, val) {
      state._suspendExternalSync = val
    },
    setLastLocalEditAt(state, val) {
      state._lastLocalEditAt = val
    },
    setPdfLoading(state, val) {
      state.pdfLoading = val
    },
    setMapillaryIsNotArrow(state, val) {
      state.mapillaryIsNotArrow = val
    },
    setHitText(state, val) {
      state.hitText = val
    },
    setConfigFeature(state, val) {
      state.configFeature = val
    },
    setSnackbarValue(state, val) {
      state.snackbar = val
    },
    setNoProgress(state, val) {
      state.noProgress = val
    },
    setMapillaryZindex(state, val) {
      state.mapillaryZindex = val
    },
    setMapillaryType(state, val) {
      state.mapillaryType = val
    },
    setMapillaryImageId(state, val) {
      state.mapillaryImageId = val
    },
    setIsMine(state, val) {
      state.isMine = val
    },
    setIsEditable(state, val) {
      state.isEditable = val
    },
    setGeojsonName(state, val) {
      state.geojsonName = val
    },
    setIsDraggingFeature(state, val) {
      state._isDraggingFeature = val
    },
    setGeometryRebuilding(state, val) {
      state._geometryRebuilding = val
    },
    setTitleDirection(state, val) {
      state.titleDirection = val
    },
    setTitleColor(state, val) {
      state.titleColor = val
    },
    setTextPx(state, val) {
      state.textPx = val
    },
    setPrintTitleText(state, val) {
      state.printTitleText = val
    },
    setOazaYomi(state, val) {
      state.oazaYomi = val
    },
    setJdpCoordinates(state, val) {
      state.jdpCoordinates = val
    },
    setJdpCode(state, val) {
      state.jdpCode = val
    },
    setStoreId(state, val) {
      state.id = val
    },
    setDrawVisible(state, val) {
      state.drawVisible = val
    },
    setDrawOpacity(state, val) {
      state.drawOpacity = val
    },
    setDrawFeature(state, val) {
      state.drawFeature = val
    },
    setAddressYomi(state, val) {
      state.addressYomi = val
    },
    setAddressNoPref(state, val) {
      state.addressNoPref = val
    },
    setAddress(state, val) {
      state.address = val
    },
    setSuppressMidpointClickOnce(state, val) {
      state._suppressMidpointClickOnce = val
    },
    setUserDrawCreated(state, val) {
      state.userDrawCreated = val
    },
    setSimaTextForUser(state, val) {
      state.simaTextForUser = val
    },
    setSimaText(state, val) {
      state.simaText = val
    },
    setSimaOpacity(state, val) {
      state.simaOpacity = val
    },
    setShortUrlTimerId(state, val) {
      state.shortUrlTimerId = val
    },
    setPrefId(state, val) {
      state.prefId = val
    },
    setMyDrawUndoCount(state, val) {
      state.myDrawUndoCount = val
    },
    setMyDrawRedoCount(state, val) {
      state.myDrawRedoCount = val
    },
    setMapillaryTytle(state, val) {
      state.mapillaryTytle = val
    },
    setMapillaryFeature(state, val) {
      state.mapillaryFeature = val
    },
    setLastShortUrlParam(state, val) {
      state.lastShortUrlParam = val
    },
    setLastHighlightedChiban(state, val) {
      state.lastHighlightedChiban = val
    },
    setLassoGeojson(state, val) {
      state.lassoGeojson = val
    },
    setIsUsingServerGeojson(state, val) {
      state.isUsingServerGeojson = val
    },
    setGeojsonId(state, val) {
      state.geojsonId = val
    },
    setDisablePollingForTest(state, val) {
      state.disablePollingForTest = val
    },
    setCad(state, val) {
      state.cad = val
    },
    // --- mapOrientationControl / screenCapture の直接書き換え排除用 ---
    setIsTerrainEnabled(state, val) {
      state.isTerrainEnabled = val
    },
    setUrlForX(state, val) {
      state.urlForX = val
    },
    // --- georeferencing / mapFocus / uploadHandlers / offlineCache の直接書き換え排除用 ---
    setPendingFile(state, val) {
      state.pendingFile = val
    },
    setKyusekiInitialFile(state, val) {
      state.kyusekiInitialFile = val
    },
    setPmtilesMaximum(state, val) {
      state.pmtilesMaximum = val
    },
    setPmtilesFirstSave(state, val) {
      state.pmtilesFirstSave = val
    },
  },
  actions: {
    // お気に入りレイヤー関連
    async toggleFavoriteLayer({ commit, state }, layerId) {
      commit('TOGGLE_FAVORITE_LAYER', layerId)

      const userId = localStorage.getItem('lastUserId') || 'guest'

      if (userId && userId !== 'guest') {
        // ログインユーザー: MySQLに保存
        try {
          const nickname = localStorage.getItem('lastNickname') || ''
          const params = new URLSearchParams()
          params.append('uid', userId)
          params.append('favorite_layer_ids', JSON.stringify(state.favoriteLayerIds))
          params.append('nickname', nickname)
          const response = await fetch('/oh3/php/userConfig.php', {
            method: 'POST',
            body: params,
          })
          const result = await response.json()
          if (result.error) {
            console.error('[toggleFavoriteLayer] MySQL保存エラー:', result.error)
            // エラー時はlocalStorageにもフォールバック
            localStorage.setItem(`oh3.favoriteLayerIds.${userId}`, JSON.stringify(state.favoriteLayerIds))
          } else {
          }
        } catch (e) {
          console.error('[toggleFavoriteLayer] MySQL保存失敗:', e)
          // エラー時はlocalStorageにフォールバック
          localStorage.setItem(`oh3.favoriteLayerIds.${userId}`, JSON.stringify(state.favoriteLayerIds))
        }
      } else {
        // ゲストユーザー: localStorageに保存
        try {
          localStorage.setItem(`oh3.favoriteLayerIds.${userId}`, JSON.stringify(state.favoriteLayerIds))
        } catch (e) {
          console.error('[toggleFavoriteLayer] localStorage保存失敗:', e)
        }
      }
    },

    async updateFavoriteLayerOrder({ commit, state }, orderedIds) {
      commit('UPDATE_FAVORITE_LAYER_ORDER', orderedIds)

      const userId = localStorage.getItem('lastUserId') || 'guest'

      if (userId && userId !== 'guest') {
        // ログインユーザー: MySQLに保存
        try {
          const nickname = localStorage.getItem('lastNickname') || ''
          const params = new URLSearchParams()
          params.append('uid', userId)
          params.append('favorite_layer_ids', JSON.stringify(orderedIds))
          params.append('nickname', nickname)
          const response = await fetch('/oh3/php/userConfig.php', {
            method: 'POST',
            body: params,
          })
          const result = await response.json()
          if (result.error) {
            console.error('[updateFavoriteLayerOrder] MySQL保存エラー:', result.error)
            localStorage.setItem(`oh3.favoriteLayerIds.${userId}`, JSON.stringify(orderedIds))
          } else {
          }
        } catch (e) {
          console.error('[updateFavoriteLayerOrder] MySQL保存失敗:', e)
          localStorage.setItem(`oh3.favoriteLayerIds.${userId}`, JSON.stringify(orderedIds))
        }
      } else {
        // ゲストユーザー: localStorageに保存
        try {
          localStorage.setItem(`oh3.favoriteLayerIds.${userId}`, JSON.stringify(orderedIds))
        } catch (e) {
          console.error('[updateFavoriteLayerOrder] localStorage保存失敗:', e)
        }
      }
    },

    async loadFavoriteLayersFromStorage({ commit }) {
      const userId = localStorage.getItem('lastUserId') || 'guest'

      if (userId && userId !== 'guest') {
        // ログインユーザー: MySQLから読み込み
        try {
          const response = await postWithUid('/oh3/php/userConfigSelect.php', userId)
          const result = await response.json()

          if (result.error) {
            console.error('[loadFavoriteLayersFromStorage] MySQL読み込みエラー:', result.error)
            // エラー時はlocalStorageからフォールバック
            const localSaved = localStorage.getItem(`oh3.favoriteLayerIds.${userId}`)
            commit('SET_FAVORITE_LAYERS', localSaved ? JSON.parse(localSaved) : [])
          } else if (result.length > 0 && result[0].favorite_layer_ids) {
            // MySQLから取得したデータをセット
            const favoriteIds = JSON.parse(result[0].favorite_layer_ids)
            commit('SET_FAVORITE_LAYERS', favoriteIds)
          } else {
            // MySQLにデータがない場合はlocalStorageから移行
            const localSaved = localStorage.getItem(`oh3.favoriteLayerIds.${userId}`)
            if (localSaved) {
              const favoriteIds = JSON.parse(localSaved)
              commit('SET_FAVORITE_LAYERS', favoriteIds)
              // MySQLにも保存して移行完了
              const nickname = localStorage.getItem('lastNickname') || ''
              const params = new URLSearchParams()
              params.append('uid', userId)
              params.append('favorite_layer_ids', JSON.stringify(favoriteIds))
              params.append('nickname', nickname)
              await fetch('/oh3/php/userConfig.php', {
                method: 'POST',
                body: params,
              })
            } else {
              commit('SET_FAVORITE_LAYERS', [])
            }
          }
        } catch (e) {
          console.error('[loadFavoriteLayersFromStorage] MySQL読み込み失敗:', e)
          // エラー時はlocalStorageからフォールバック
          const localSaved = localStorage.getItem(`oh3.favoriteLayerIds.${userId}`)
          commit('SET_FAVORITE_LAYERS', localSaved ? JSON.parse(localSaved) : [])
        }
      } else {
        // ゲストユーザー: localStorageから読み込み
        try {
          const localSaved = localStorage.getItem(`oh3.favoriteLayerIds.${userId}`)
          commit('SET_FAVORITE_LAYERS', localSaved ? JSON.parse(localSaved) : [])
        } catch (e) {
          console.error('[loadFavoriteLayersFromStorage] localStorage読み込み失敗:', e)
          commit('SET_FAVORITE_LAYERS', [])
        }
      }
    },
    // 最近使用したレイヤー関連
    async addRecentLayer({ commit, state }, layerId) {
      commit('ADD_RECENT_LAYER', layerId)

      const userId = localStorage.getItem('lastUserId') || 'guest'

      if (userId && userId !== 'guest') {
        // ログインユーザー: MySQLに保存
        try {
          const nickname = localStorage.getItem('lastNickname') || ''
          const params = new URLSearchParams()
          params.append('uid', userId)
          params.append('recent_layer_ids', JSON.stringify(state.recentLayerIds))
          params.append('nickname', nickname)
          const response = await fetch('/oh3/php/userConfig.php', {
            method: 'POST',
            body: params,
          })
          const result = await response.json()
          if (result.error) {
            console.error('[addRecentLayer] MySQL保存エラー:', result.error)
            // エラー時はlocalStorageにもフォールバック
            localStorage.setItem('oh3.recentLayerIds', JSON.stringify(state.recentLayerIds))
          }
        } catch (e) {
          console.error('[addRecentLayer] MySQL保存失敗:', e)
          // エラー時はlocalStorageにフォールバック
          localStorage.setItem('oh3.recentLayerIds', JSON.stringify(state.recentLayerIds))
        }
      } else {
        // ゲストユーザー: localStorageに保存
        try {
          localStorage.setItem('oh3.recentLayerIds', JSON.stringify(state.recentLayerIds))
        } catch (e) {
          console.error('[addRecentLayer] localStorage保存失敗:', e)
        }
      }
    },

    async loadRecentLayersFromStorage({ commit }) {
      const userId = localStorage.getItem('lastUserId') || 'guest'

      if (userId && userId !== 'guest') {
        // ログインユーザー: MySQLから読み込み
        try {
          const response = await postWithUid('/oh3/php/userConfigSelect.php', userId)
          const result = await response.json()

          if (result.error) {
            console.error('[loadRecentLayersFromStorage] MySQL読み込みエラー:', result.error)
            // エラー時はlocalStorageからフォールバック
            const localSaved = localStorage.getItem('oh3.recentLayerIds')
            commit('SET_RECENT_LAYERS', localSaved ? JSON.parse(localSaved) : [])
          } else if (result.length > 0 && result[0].recent_layer_ids) {
            // MySQLから取得したデータをセット
            const recentIds = JSON.parse(result[0].recent_layer_ids)
            commit('SET_RECENT_LAYERS', recentIds)
          } else {
            // MySQLにデータがない場合はlocalStorageから移行
            const localSaved = localStorage.getItem('oh3.recentLayerIds')
            if (localSaved) {
              const recentIds = JSON.parse(localSaved)
              commit('SET_RECENT_LAYERS', recentIds)
              // MySQLにも保存して移行完了
              const nickname = localStorage.getItem('lastNickname') || ''
              const params = new URLSearchParams()
              params.append('uid', userId)
              params.append('recent_layer_ids', JSON.stringify(recentIds))
              params.append('nickname', nickname)
              await fetch('/oh3/php/userConfig.php', {
                method: 'POST',
                body: params,
              })
            } else {
              commit('SET_RECENT_LAYERS', [])
            }
          }
        } catch (e) {
          console.error('[loadRecentLayersFromStorage] MySQL読み込み失敗:', e)
          // エラー時はlocalStorageからフォールバック
          const localSaved = localStorage.getItem('oh3.recentLayerIds')
          commit('SET_RECENT_LAYERS', localSaved ? JSON.parse(localSaved) : [])
        }
      } else {
        // ゲストユーザー: localStorageから読み込み
        try {
          const localSaved = localStorage.getItem('oh3.recentLayerIds')
          commit('SET_RECENT_LAYERS', localSaved ? JSON.parse(localSaved) : [])
        } catch (e) {
          console.error('[loadRecentLayersFromStorage] localStorage読み込み失敗:', e)
          commit('SET_RECENT_LAYERS', [])
        }
      }
    },

    // 検索履歴（住所検索用）
    async loadSearchHistory({ commit }) {
      const userId = localStorage.getItem('lastUserId') || 'guest'
      if (!userId || userId === 'guest') {
        commit('SET_SEARCH_HISTORY', [])
        return
      }
      try {
        const response = await postWithUid('/oh3/php/userConfigSelect.php', userId)
        const result = await response.json()

        if (result?.error) {
          console.error('[loadSearchHistory] MySQL読み込みエラー:', result.error)
          commit('SET_SEARCH_HISTORY', [])
          return
        }
        if (Array.isArray(result) && result.length > 0 && result[0].search_history) {
          const history = JSON.parse(result[0].search_history)
          commit('SET_SEARCH_HISTORY', Array.isArray(history) ? history : [])
        } else {
          commit('SET_SEARCH_HISTORY', [])
        }
      } catch (e) {
        console.error('[loadSearchHistory] MySQL読み込み失敗:', e)
        commit('SET_SEARCH_HISTORY', [])
      }
    },

    async saveSearchHistory({ state }) {
      const userId = localStorage.getItem('lastUserId') || 'guest'
      if (!userId || userId === 'guest') return
      try {
        const nickname = localStorage.getItem('lastNickname') || ''
        const params = new URLSearchParams()
        params.append('uid', userId)
        params.append('search_history', JSON.stringify(state.searchHistory))
        params.append('nickname', nickname)
        await fetch('/oh3/php/userConfig.php', {
          method: 'POST',
          body: params,
        })
      } catch (e) {
        console.error('[saveSearchHistory] MySQL保存失敗:', e)
      }
    },

    async addSearchHistory({ commit, dispatch }, term) {
      const userId = localStorage.getItem('lastUserId') || 'guest'
      if (!userId || userId === 'guest') {
        // 非ログイン者は履歴機能なし
        return
      }
      commit('ADD_SEARCH_HISTORY_TERM', term)
      await dispatch('saveSearchHistory')
    },
    async removeSearchHistory({ commit, dispatch }, term) {
      const userId = localStorage.getItem('lastUserId') || 'guest'
      if (!userId || userId === 'guest') {
        return
      }
      commit('REMOVE_SEARCH_HISTORY_TERM', term)
      await dispatch('saveSearchHistory')
    },

    // お気に入り地点
    async loadFavoriteLocations({ commit }) {
      const userId = localStorage.getItem('lastUserId') || 'guest'
      if (!userId || userId === 'guest') {
        commit('SET_FAVORITE_LOCATIONS', [])
        return
      }
      try {
        const response = await postWithUid('/oh3/php/userConfigSelect.php', userId)
        const result = await response.json()
        if (result?.error) {
          console.error('[loadFavoriteLocations] MySQL読み込みエラー:', result.error)
          commit('SET_FAVORITE_LOCATIONS', [])
          return
        }
        if (Array.isArray(result) && result.length > 0 && result[0].favorite_locations) {
          const locations = JSON.parse(result[0].favorite_locations)
          commit('SET_FAVORITE_LOCATIONS', Array.isArray(locations) ? locations : [])
        } else {
          commit('SET_FAVORITE_LOCATIONS', [])
        }
      } catch (e) {
        console.error('[loadFavoriteLocations] MySQL読み込み失敗:', e)
        commit('SET_FAVORITE_LOCATIONS', [])
      }
    },

    async saveFavoriteLocations({ state }) {
      const userId = localStorage.getItem('lastUserId') || 'guest'
      if (!userId || userId === 'guest') return
      const nickname = localStorage.getItem('myNickname') || ''
      try {
        const params = new URLSearchParams()
        params.append('uid', userId)
        params.append('favorite_locations', JSON.stringify(state.favoriteLocations))
        params.append('nickname', nickname)
        await fetch('/oh3/php/userConfig.php', {
          method: 'POST',
          body: params,
        })
      } catch (e) {
        console.error('[saveFavoriteLocations] MySQL保存失敗:', e)
      }
    },

    async addFavoriteLocation({ commit, dispatch }, location) {
      const userId = localStorage.getItem('lastUserId') || 'guest'
      if (!userId || userId === 'guest') return
      commit('ADD_FAVORITE_LOCATION', location)
      await dispatch('saveFavoriteLocations')
    },

    async updateFavoriteLocation({ commit, dispatch }, { oldName, newLocation }) {
      const userId = localStorage.getItem('lastUserId') || 'guest'
      if (!userId || userId === 'guest') return
      commit('UPDATE_FAVORITE_LOCATION', { oldName, newLocation })
      await dispatch('saveFavoriteLocations')
    },

    async removeFavoriteLocation({ commit, dispatch }, name) {
      const userId = localStorage.getItem('lastUserId') || 'guest'
      if (!userId || userId === 'guest') return
      commit('REMOVE_FAVORITE_LOCATION', name)
      await dispatch('saveFavoriteLocations')
    },

    // 保存マーカー
    async loadSavedMarkers({ commit }) {
      const userId = localStorage.getItem('lastUserId') || 'guest'
      if (!userId || userId === 'guest') {
        commit('SET_SAVED_MARKERS', [])
        return
      }
      try {
        const response = await postWithUid('/oh3/php/userConfigSelect.php', userId)
        const result = await response.json()
        if (result?.error) {
          console.error('[loadSavedMarkers] MySQL読み込みエラー:', result.error)
          commit('SET_SAVED_MARKERS', [])
          return
        }
        if (Array.isArray(result) && result.length > 0 && result[0].saved_markers) {
          const markers = JSON.parse(result[0].saved_markers)
          commit('SET_SAVED_MARKERS', Array.isArray(markers) ? markers : [])
        } else {
          commit('SET_SAVED_MARKERS', [])
        }
      } catch (e) {
        console.error('[loadSavedMarkers] MySQL読み込み失敗:', e)
        commit('SET_SAVED_MARKERS', [])
      }
    },

    async saveSavedMarkers({ state }) {
      const userId = localStorage.getItem('lastUserId') || 'guest'
      if (!userId || userId === 'guest') return
      try {
        const params = new URLSearchParams()
        params.append('uid', userId)
        params.append('saved_markers', JSON.stringify(state.savedMarkers))
        await fetch('/oh3/php/userConfig.php', {
          method: 'POST',
          body: params,
        })
      } catch (e) {
        console.error('[saveSavedMarkers] MySQL保存失敗:', e)
      }
    },

    async addSavedMarker({ commit, dispatch }, marker) {
      const userId = localStorage.getItem('lastUserId') || 'guest'
      if (!userId || userId === 'guest') return null
      // ユニークIDを生成
      const markerWithId = {
        ...marker,
        id: `marker_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        created_at: new Date().toISOString(),
      }
      commit('ADD_SAVED_MARKER', markerWithId)
      await dispatch('saveSavedMarkers')
      return markerWithId
    },

    async removeSavedMarker({ commit, dispatch }, markerId) {
      const userId = localStorage.getItem('lastUserId') || 'guest'
      if (!userId || userId === 'guest') return
      commit('REMOVE_SAVED_MARKER', markerId)
      await dispatch('saveSavedMarkers')
    },

    // フローティングウィンドウ関連
    showFloatingWindow({ commit }, id) {
      commit('setFloatingVisible', { id, visible: true })
    },
    hideFloatingWindow({ commit }, id) {
      commit('setFloatingVisible', { id, visible: false })
    },
    toggleFloatingWindow({ commit, state }, id) {
      const currentVisible = state.floatingWindows[id] || false
      commit('setFloatingVisible', { id, visible: !currentVisible })
    },
    triggerSnackbarForGroup({ commit }, { message }) {
      commit('showSnackbarForGroup', message)
      setTimeout(() => commit('hideSnackbarForGroup'), 3000) // timeout と一致
    },
    async deleteSelectedPointFromFirestore({ state }) {
      const groupId = state.currentGroupName
      if (!groupId) return

      const idToDelete = state.selectedPointFeature?.properties?.id
      if (!idToDelete) return

      const doc = await db.collection('groups').doc(groupId).get()
      if (!doc.exists) return

      const data = doc.data()
      const features = data.layers?.points?.features || []

      const newFeatures = features.filter((f) => f.properties?.id !== idToDelete)

      await db
        .collection('groups')
        .doc(groupId)
        .set(
          {
            layers: {
              points: {
                type: 'FeatureCollection',
                features: newFeatures,
              },
            },
            lastModifiedBy: state.userId,
            lastModifiedAt: Date.now(),
          },
          { merge: true }
        )

    },
    async fetchGroupData({ commit }, groupId) {
      const doc = await db.collection('groups').doc(groupId).get()
      if (doc.exists) {
        const data = doc.data()
        if (data.layers?.points) {
          commit('setGroupGeojson', data.layers.points)
        }
      }
    },
    async saveSelectedPointToFirestore({ state }) {
      const db = firebase.firestore()
      const groupId = state.currentGroupId
      const layerId = state.selectedLayerId // ← こっちを必ず使う
      const feature = state.selectedPointFeature


      if (!groupId || !layerId || !feature) {
        console.warn('groupId, layerId, または feature が未設定です')
        return
      }

      try {
        const docRef = db.collection('groups').doc(groupId).collection('layers').doc(layerId)
        const doc = await docRef.get()
        let features = doc.exists ? doc.data().features || [] : []

        const index = features.findIndex((f) => f.properties.id === feature.properties.id)
        if (index >= 0) {
          features[index] = feature // 更新
        } else {
          features.push(feature) // 新規追加
        }

        await docRef.set(
          {
            features,
            lastModifiedAt: firebase.firestore.FieldValue.serverTimestamp(),
          },
          { merge: true }
        )

      } catch (error) {
        console.error('Firestore 保存エラー:', error)
      }
    },
    async saveSelectedPointFeatureToFirestore({ state, commit }) {
      const feature = state.selectedPointFeature
      const groupId = state.currentGroupName

      if (!feature || !feature.properties?.id || !groupId) return

      // Vuex 側の groupGeojson を更新
      commit('saveSelectedPointFeature')

      // Firestore に保存
      await db
        .collection('groups')
        .doc(groupId)
        .set(
          {
            layers: {
              points: JSON.parse(JSON.stringify(state.groupGeojson)),
            },
            lastModifiedBy: state.userId,
            lastModifiedAt: Date.now(),
          },
          { merge: true }
        )

    },
    updateSelectedPointColor(state, color) {
      if (state.selectedPointFeature) {
        state.selectedPointFeature.properties.color = color
        state.selectedPointColor = color
      }
    },
    setSelectedPointFeature(state, feature) {
      state.selectedPointFeature = feature
      // ✅ 選択時に色を反映
      state.selectedPointColor = feature?.properties?.color || '#ff0000'
    },
    // Welcome Wizard actions
    async checkAndShowWizard({ commit, state }) {
      // 常に非表示
      commit('SET_WIZARD_COMPLETED', true)
      return false
    },
    async completeWizard({ commit, state }) {
      // 1. ログインユーザーならFirestoreに保存
      if (state.userId) {
        try {
          await db.collection('users').doc(state.userId).set(
            {
              wizardCompleted: true,
              wizardCompletedAt: new Date(),
              wizardPurpose: state.welcomeWizard.selectedPurpose,
            },
            { merge: true }
          )
        } catch (e) {
          console.warn('Failed to save wizard completion to Firestore:', e)
        }
      }

      // 2. 常にlocalStorageにも保存（次回の高速判定用）
      localStorage.setItem('oh3_wizard_completed', '1')

      // 3. Vuexの状態を更新
      commit('SET_WIZARD_COMPLETED', true)
      commit('SET_WIZARD_OPEN', false)
    },
    openWizard({ commit }) {
      commit('RESET_WIZARD')
      commit('SET_WIZARD_OPEN', true)
    },
    closeWizard({ commit }) {
      commit('SET_WIZARD_OPEN', false)
    },
    nextWizardStep({ commit, state }) {
      const nextStep = state.welcomeWizard.currentStep + 1
      if (nextStep < state.welcomeWizard.totalSteps) {
        commit('SET_WIZARD_STEP', nextStep)
      }
    },
    prevWizardStep({ commit, state }) {
      const prevStep = state.welcomeWizard.currentStep - 1
      if (prevStep >= 0) {
        commit('SET_WIZARD_STEP', prevStep)
      }
    },
    setWizardPurpose({ commit }, purpose) {
      commit('SET_WIZARD_PURPOSE', purpose)
    },
  },
  modules: {
    messageDialog: messageDialogModule,
  },
})
